import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { Button } from '../../../components/ui/button';
import { toast } from '../../../lib/toast-utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { RTLInput, RTLTextarea } from '../../../components/ui/rtl-input';
import { isRTL, getDirAttribute } from '../../../utils/rtl';
import { RTLIndicator } from '../../../components/ui/rtl-indicator';
import { HeroSection } from './sections/HeroSection';
import { ProjectHighlightsSection } from './sections/ProjectHighlightsSection';
import { ProjectOverviewSection } from './sections/ProjectOverviewSection';
import { GallerySection } from './sections/GallerySection';
import { PaymentPlanSection } from './sections/PaymentPlanSection';
import { LocationSection } from './sections/LocationSection';
import { FaqSection } from './sections/FaqSection';
import { LegalInfoSection } from './sections/LegalInfoSection';
import NavigationSettings from '../NavigationSettings';
import { Id } from '../../../convex/_generated/dataModel';
import { api } from '../../../convex/_generated/api';
import { BackgroundImageUploader } from './BackgroundImageUploader';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Loader2, Languages, AlertTriangle } from 'lucide-react';
import { ImageUploader } from '../../ui/image-uploader';
import { useComponentImages } from '../../../hooks/useComponentImages';
import { useAuth } from '../../../lib/auth/AuthContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { AIButton } from '../../ui/ai-button';
import { KnowledgeModal } from '../../ui/knowledge-modal';
import { useAIGeneration } from '../../../hooks/useAIGeneration';
import { FieldType } from '../../../lib/ai/characterLimits';
import {
  FileId,
  UnitType,
  SellingPoint,
  ProjectOverviewContent,
  Feature,
  Developer,
  ProjectHighlightsContent,
  PaymentPlanContent,
  LocationContent,
  GalleryContent,
  LeadFormContent,
  DownloadFormContent,
  EmailTemplateContent,
  HeroContent,
  FaqContent,
  LegalInfoContent
} from '../../../types/pageBuilder';
import {
  DEFAULT_HERO_CONTENT,
  DEFAULT_PROJECT_OVERVIEW_CONTENT,
  DEFAULT_PROJECT_HIGHLIGHTS_CONTENT,
  DEFAULT_FAQ_CONTENT,
  DEFAULT_LOCATION_CONTENT,
  DEFAULT_PAYMENT_PLAN_CONTENT,
  DEFAULT_GALLERY_CONTENT,
  DEFAULT_LEGAL_INFO_CONTENT,
  INITIAL_LEAD_FORM_CONTENT,
  INITIAL_DOWNLOAD_FORM_CONTENT,
  DEFAULT_LEAD_FORM_CONTENT,
  DEFAULT_DOWNLOAD_FORM_CONTENT
} from '../../../constants/pageBuilderDefaults';

export const PageBuilder: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { useAuthenticatedQuery, useAuthenticatedMutation } = useAuth();
  
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [activeTab, setActiveTab] = useState('hero');
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Hero component state
  const [heroContent, setHeroContent] = useState<HeroContent>(DEFAULT_HERO_CONTENT);
  
  // State for project overview content
  const [projectOverviewContent, setProjectOverviewContent] = useState<ProjectOverviewContent>(DEFAULT_PROJECT_OVERVIEW_CONTENT);
  
  // State for project highlights content
  const [projectHighlightsContent, setProjectHighlightsContent] = useState<ProjectHighlightsContent>(DEFAULT_PROJECT_HIGHLIGHTS_CONTENT);
  
  // State for FAQ content
  const [faqContent, setFaqContent] = useState<FaqContent>(DEFAULT_FAQ_CONTENT);
  
  // State for Location content
  const [locationContent, setLocationContent] = useState<LocationContent>(DEFAULT_LOCATION_CONTENT);
  
  // State for Payment Plan content
  const [paymentPlanContent, setPaymentPlanContent] = useState<PaymentPlanContent>(DEFAULT_PAYMENT_PLAN_CONTENT);
  
  // State for Project Gallery content
  const [galleryContent, setGalleryContent] = useState<GalleryContent>(DEFAULT_GALLERY_CONTENT);
  
  // State for Legal Info content
  const [legalInfoContent, setLegalInfoContent] = useState<LegalInfoContent>(DEFAULT_LEGAL_INFO_CONTENT);
  
  // State for Lead Form content
  const [leadFormContent, setLeadFormContent] = useState<LeadFormContent>(INITIAL_LEAD_FORM_CONTENT);

  // State for Download Form content
  const [downloadFormContent, setDownloadFormContent] = useState<DownloadFormContent>(INITIAL_DOWNLOAD_FORM_CONTENT);
  
  // Add refreshTrigger state before it's used
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Add a timestamp state to force query refresh
  const [queryTimestamp, setQueryTimestamp] = useState(Date.now());
  
  // API Queries using authenticated helpers
  const project = useAuthenticatedQuery('projects:getById', projectId ? { projectId: projectId as Id<"projects"> } : "skip") || null;
  const languages = useQuery(api.languages.getActive, {}) || [];
  
  // Debug the components query with timestamp for forced refresh
  const componentsQueryArgs = projectId && selectedLanguage ? { 
    projectId: projectId as Id<"projects">, 
    languageCode: selectedLanguage,
    timestamp: queryTimestamp 
  } : "skip";
  console.log('Components query args:', { projectId, selectedLanguage, timestamp: queryTimestamp, args: componentsQueryArgs });
  
  const components = useAuthenticatedQuery(
    'pageComponents:getByProjectAndLanguage', 
    componentsQueryArgs
  ) || [];
  
  // Authenticated mutations
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');
  const generateUploadUrl = useAuthenticatedMutation('filesFunctions:generateUploadUrlFn');
  const saveFileMetadata = useAuthenticatedMutation('filesFunctions:saveFileMetadataFn');
  
  // Find existing components - memoize to prevent re-renders
  const heroComponent = useMemo(() => 
    components.find(c => c.componentType === 'hero'),
    [components]
  );
  
  const projectOverviewComponent = useMemo(() => 
    components.find(c => c.componentType === 'projectOverview'),
    [components]
  );
  
  const projectHighlightsComponent = useMemo(() => 
    components.find(c => c.componentType === 'projectHighlights'),
    [components]
  );
  
  const faqComponent = useMemo(() => 
    components.find(c => c.componentType === 'faq'),
    [components]
  );
  
  const locationComponent = useMemo(() => 
    components.find(c => c.componentType === 'location'),
    [components]
  );
  
  const paymentPlanComponent = useMemo(() => 
    components.find(c => c.componentType === 'paymentPlan'),
    [components]
  );
  
  const galleryComponent = useMemo(() => 
    components.find(c => c.componentType === 'projectGallery'),
    [components]
  );
  
  const legalInfoComponent = useMemo(() => 
    components.find(c => c.componentType === 'legalInfo'),
    [components]
  );
  
  const leadFormComponent = useMemo(() => 
    components.find(c => c.componentType === 'leadForm'),
    [components]
  );

  const downloadFormComponent = useMemo(() => 
    components.find(c => c.componentType === 'downloadForm'),
    [components]
  );
  
  // Use the component images hook to get image URLs
  const { getImageUrl } = useComponentImages(
    projectId ? projectId as Id<"projects"> : undefined,
    heroComponent?._id
  );
  
  // Add translation hook
  const { translate, isTranslating, error: translationError, result: translationResult, reset: resetTranslation } = useTranslation({
    onSuccess: (result) => {
      toast({
        title: 'Translation Complete',
        description: `Successfully translated ${result.updatedComponents} components to ${selectedLanguage}`,
      });
      // Force refresh by updating timestamp and trigger
      setTimeout(() => {
        setQueryTimestamp(Date.now());
        setRefreshTrigger(prev => prev + 1);
      }, 500); // Give Convex time to propagate the changes
    },
    onError: (error) => {
      toast({
        title: 'Translation Failed',
        description: error,
        variant: 'destructive',
      });
    },
  });

  // Translation handler
  const handleTranslateToLanguage = async (targetLanguage: string) => {
    if (!projectId) {
      toast({
        title: 'Error',
        description: 'Project ID is missing',
        variant: 'destructive',
      });
      return;
    }

    if (targetLanguage === 'en') {
      toast({
        title: 'Translation Not Needed',
        description: 'English is the source language for translations',
        variant: 'default',
      });
      return;
    }

    try {
      await translate(projectId as Id<"projects">, targetLanguage);
    } catch (error) {
      console.error('Translation error:', error);
      // Error is already handled by the onError callback
    }
  };
  
  // Memoize image URLs to prevent unnecessary re-renders
  const heroBackgroundImageUrl = useMemo(() => {
    if (heroComponent?.content?.backgroundImageId) {
      const url = getImageUrl(heroComponent.content.backgroundImageId) || 
             heroComponent.content.backgroundImage || '';
      
      console.log('Resolved hero background image URL:', {
        backgroundImageId: heroComponent.content.backgroundImageId,
        resolvedUrl: url,
        originalUrl: heroComponent.content.backgroundImage
      });
      
      return url;
    }
    return heroComponent?.content?.backgroundImage || '';
  }, [heroComponent, getImageUrl]);
  
  // Memoize unit type image URLs
  const updatedUnitTypes = useMemo(() => {
    if (!projectOverviewComponent?.content?.unitTypes) return [];
    
    return projectOverviewComponent.content.unitTypes.map((unit: UnitType) => {
      if (unit.imageId) {
        const updatedImageUrl = getImageUrl(unit.imageId);
        if (updatedImageUrl) {
          return { ...unit, image: updatedImageUrl };
        }
      }
      return unit;
    });
  }, [projectOverviewComponent, getImageUrl]);

  // Function to trigger a refresh of components
  const refreshComponents = () => {
    console.log('Triggering component refresh');
    
    // Force query refresh by updating timestamp
    setQueryTimestamp(Date.now());
    
    // Increment the refresh trigger to trigger the useEffect
    setRefreshTrigger(prev => prev + 1);
    
    // Note: Convex React automatically invalidates queries when mutations occur,
    // but we set a loading state to provide visual feedback and ensure UI updates
  };

  // Use an effect to refetch when the trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      // Skip any auto-refresh actions during initial loading
      if (!projectId || !selectedLanguage) return;

      console.log('Refreshing components data for:', { projectId, selectedLanguage });
      
      // Show loading state briefly to provide user feedback
      setIsLoading(true);
      
      // Allow a longer timeout for Convex to complete the mutation and update its cache
      // This is especially important after translation operations
      const timer = setTimeout(() => {
        // Convex queries automatically update when data changes
        // We just need to stop the loading indicator
        setIsLoading(false);
        console.log('Component refresh completed');
      }, 1000); // Longer timeout to ensure translation changes propagate
      
      return () => clearTimeout(timer);
    }
  }, [refreshTrigger, projectId, selectedLanguage]);

  // Debug useEffect to log component loading
  useEffect(() => {
    console.log('Components loaded:', {
      projectId,
      selectedLanguage,
      queryTimestamp,
      componentsCount: components.length,
      heroComponent: heroComponent ? 'found' : 'not found',
      heroContent: heroComponent?.content,
      components: components.map(c => ({ type: c.componentType, id: c._id, language: c.languageCode }))
    });
  }, [components, heroComponent, projectId, selectedLanguage, queryTimestamp]);
  
  // Test function to diagnose issues with files
  const testFileUpload = async () => {
    if (!projectId) {
      toast({
        title: 'Error',
        description: 'Project ID is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Testing file storage operations...');
    setIsLoading(true);
    
    try {
      // 1. Generate an upload URL
      console.log('Generating upload URL...');
      const uploadUrl = await generateUploadUrl({
        contentType: 'image/png',
        maxSize: 1024 * 100, // 100KB
      });
      
      console.log('Upload URL generated successfully');
      
      // Create a small test image - a 1x1 black pixel
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, 1, 1);
      }
      
      // Convert canvas to blob
      const testImageBlob = await new Promise<Blob | null>(resolve => {
        canvas.toBlob(resolve, 'image/png');
      });
      
      if (!testImageBlob) {
        throw new Error('Failed to create test image');
      }
      
      // Upload the test image
      console.log('Uploading test image...');
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'image/png' },
        body: testImageBlob,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed:', response.status, errorText);
        throw new Error(`Upload failed with status ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Test file uploaded successfully');
      
      if (!result.storageId) {
        throw new Error('No storage ID returned from upload');
      }
      
      // Save file metadata
      console.log('Saving test file metadata...');
      const { fileId } = await saveFileMetadata({
        storageId: result.storageId,
        filename: 'test-pixel.png',
        contentType: 'image/png',
        size: testImageBlob.size,
        projectId: projectId as Id<"projects">,
        purpose: 'test',
        altText: 'Test file',
      });
      
      console.log('Test file metadata saved with ID:', fileId);
      
      toast({
        title: 'Test Successful',
        description: 'File upload test completed successfully'
      });
    } catch (error) {
      console.error('File test failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'File test failed'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Test function to create a complete test file
  const testCompleteFileUpload = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Testing complete file process...');
    setIsLoading(true);
    
    try {
      // This would be a full test that tries to save file metadata directly
      // without actually uploading a file (which requires a browser file input)
      const testStorageId = "placeholder" as unknown as Id<"_storage">;
      console.log('Test not implemented - requires real storage ID');
      
      toast({
        title: 'Not Implemented',
        description: 'Full file test requires an actual file upload'
      });
    } catch (error) {
      console.error('Complete file test failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Complete file test failed'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Test function to diagnose issues
  const runTestComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Creating a simple test component');
    setIsLoading(true);
    
    try {
      // Use the normal upsert function but with simple test data
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'test',
        order: 999,
        content: { test: true, timestamp: Date.now() },
        isActive: true
      });
      
      console.log('Test component created with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Test component created successfully'
      });
    } catch (error) {
      console.error('Test failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Test failed'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Brochure upload functions
  const handleBrochureFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF only)
    if (file.type !== 'application/pdf') {
      toast({
        title: 'Invalid File Type',
        description: 'Please select a PDF file for the brochure.',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast({
        title: 'File Too Large',
        description: 'Brochure file must be smaller than 10MB.',
        variant: 'destructive',
      });
      return;
    }

    setBrochureFile(file);
    setBrochureFileName(file.name);
  };

  const uploadBrochureFile = async () => {
    if (!brochureFile || !projectId) return;

    setIsBrochureUploading(true);
    setBrochureUploadProgress(0);

    try {
      // Step 1: Generate upload URL
      console.log('Generating upload URL for brochure...');
      const uploadUrl = await generateUploadUrl({
        contentType: brochureFile.type,
        maxSize: brochureFile.size,
      });

      if (!uploadUrl) {
        throw new Error('Failed to generate upload URL');
      }

      setBrochureUploadProgress(30);

      // Step 2: Upload file to Convex storage
      console.log('Uploading brochure to Convex storage...');
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": brochureFile.type },
        body: brochureFile,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Upload failed with status: ${response.status}`, errorText);
        throw new Error(`Upload failed with status ${response.status}: ${errorText}`);
      }

      setBrochureUploadProgress(60);

      const uploadResult = await response.json();
      console.log('Brochure uploaded successfully, received result:', uploadResult);

      if (!uploadResult.storageId) {
        console.error('Missing storageId in response:', uploadResult);
        throw new Error('No storage ID returned from upload');
      }

      // Step 3: Save file metadata
      console.log('Saving brochure metadata to database...');
      const result = await saveFileMetadata({
        storageId: uploadResult.storageId,
        filename: brochureFile.name,
        contentType: brochureFile.type,
        size: brochureFile.size,
        projectId: projectId as Id<"projects">,
        purpose: 'brochure'
      });

      setBrochureUploadProgress(100);
      setBrochureFileId(result.fileId);
      
      console.log('Brochure upload completed successfully with file ID:', result.fileId);
      
      toast({
        title: 'Success',
        description: 'Brochure uploaded successfully!',
      });

    } catch (error) {
      console.error('Brochure upload failed:', error);
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'Failed to upload brochure',
        variant: 'destructive',
      });
    } finally {
      setIsBrochureUploading(false);
      setBrochureUploadProgress(0);
    }
  };

  const removeBrochureFile = () => {
    setBrochureFile(null);
    setBrochureFileId(null);
    setBrochureFileName('');
    setBrochureUploadProgress(0);
  };

  // AI Helper Functions
  const handleSaveProjectKnowledge = async (knowledge: string) => {
    setProjectKnowledge(knowledge);
    // Here you could also save to Convex if needed
    // await saveProjectKnowledge({ projectId, knowledge });
  };

  const handleGenerateTitle = async () => {
    const prompt = "Generate a compelling title for this real estate project";
    const generatedText = await generateAI({
      prompt,
      context: projectKnowledge,
      fieldType: 'title'
    });
    
    if (generatedText) {
      setHeroContent(prev => ({ ...prev, title: generatedText }));
    }
  };

  const handleGenerateSubtitle = async () => {
    const prompt = "Generate an engaging subtitle for this real estate project";
    const generatedText = await generateAI({
      prompt,
      context: projectKnowledge,
      fieldType: 'description'
    });
    
    if (generatedText) {
      setHeroContent(prev => ({ ...prev, subtitle: generatedText }));
    }
  };

  const handleGenerateHighlight = async (index: number) => {
    const prompt = "Generate a compelling highlight point for this real estate project";
    const generatedText = await generateAI({
      prompt,
      context: projectKnowledge,
      fieldType: 'sellingPoint', // Will be mapped to FieldType.SELLING_POINT_TITLE in useAIGeneration
      language: selectedLanguage,
      validateCharacterLimits: true
    });
    
    if (generatedText) {
      updateHighlight(index, generatedText);
    }
  };

  // Project Highlights AI Functions  
  const handleGenerateFeatureTitle = async (index: number) => {
    const prompt = "Generate a property feature or amenity name";
    const generatedText = await generateAI({
      prompt,
      context: projectKnowledge,
      fieldType: FieldType.FEATURE_TITLE,
      language: selectedLanguage,
      validateCharacterLimits: true
    });
    
    if (generatedText) {
      updateFeature(index, 'title', generatedText);
    }
  };

  const handleGenerateFeatureDescription = async (index: number) => {
    const prompt = "Generate a detailed feature description highlighting the benefit to residents";
    const generatedText = await generateAI({
      prompt,
      context: projectKnowledge,
      fieldType: FieldType.FEATURE_DESCRIPTION,
      language: selectedLanguage,
      validateCharacterLimits: true
    });
    
    if (generatedText) {
      updateFeature(index, 'description', generatedText);
    }
  };

  const handleGenerateDeveloperDescription = async () => {
    const prompt = "Generate a professional developer description highlighting their credibility and experience";
    const generatedText = await generateAI({
      prompt,
      context: projectKnowledge,
      fieldType: FieldType.DEVELOPER_DESCRIPTION,
      language: selectedLanguage,
      validateCharacterLimits: true
    });
    
    if (generatedText) {
      updateDeveloper('description', generatedText);
    }
  };

  // Set initial state if there's an existing background image
  useEffect(() => {
    // Skip if heroComponent is still undefined (loading)
    if (heroComponent === undefined) return;
    
    if (heroComponent) {
      console.log('Setting hero content from existing component:', heroComponent);
      
      setHeroContent({
        ...heroComponent.content,
        highlights: heroComponent.content.highlights || ['', '', '', ''],
        backgroundImage: heroBackgroundImageUrl,
        backgroundImageId: heroComponent.content.backgroundImageId || undefined,
        primaryButtonText: heroComponent.content.primaryButtonText || 'Download Brochure',
        secondaryButtonText: heroComponent.content.secondaryButtonText || 'Speak to Property Expert',
        showDownloadForm: heroComponent.content.showDownloadForm !== undefined ? 
          heroComponent.content.showDownloadForm : true
      });
    } else {
      setHeroContent({
        title: '',
        subtitle: '',
        highlights: ['', '', '', ''],
        backgroundImage: '',
        backgroundImageId: undefined,
        primaryButtonText: 'Download Brochure',
        secondaryButtonText: 'Speak to Property Expert',
        showDownloadForm: true
      });
    }
  }, [heroComponent, heroBackgroundImageUrl, selectedLanguage]);
  
  // Update the hero content background image when heroBackgroundImageUrl changes
  useEffect(() => {
    if (!heroBackgroundImageUrl || !heroComponent) return;
    
    console.log('Updating hero content with latest background image URL:', heroBackgroundImageUrl);
    setHeroContent(prev => ({
      ...prev,
      backgroundImage: heroBackgroundImageUrl
    }));
  }, [heroBackgroundImageUrl, heroComponent]);
  
  // Initialize project overview form with data from existing component or defaults
  useEffect(() => {
    // Skip if projectOverviewComponent is still undefined (loading)
    if (projectOverviewComponent === undefined) return;
    
    if (projectOverviewComponent) {
      console.log('Setting project overview content from existing component:', projectOverviewComponent);
      
      setProjectOverviewContent({
        ...projectOverviewComponent.content,
        sellingPoints: projectOverviewComponent.content.sellingPoints || [],
        unitTypes: updatedUnitTypes
      });
    } else {
      setProjectOverviewContent({
        title: '',
        status: 'Off-Plan',
        description: '',
        sellingPoints: [],
        unitTypes: []
      });
    }
  }, [projectOverviewComponent, updatedUnitTypes, selectedLanguage]);

  // Set default language when languages are loaded
  useEffect(() => {
    console.log('Language selection effect:', { 
      languagesCount: languages.length, 
      selectedLanguage, 
      languages: languages.map(l => ({ code: l.code, isDefault: l.isDefault }))
    });
    
    if (languages.length > 0 && !selectedLanguage) {
      const defaultLang = languages.find(l => l.isDefault);
      if (defaultLang) {
        console.log('Setting default language:', defaultLang.code);
        setSelectedLanguage(defaultLang.code);
      } else if (languages[0]) {
        console.log('Setting first language as default:', languages[0].code);
        setSelectedLanguage(languages[0].code);
      }
    }
  }, [languages, selectedLanguage]);

  // Initialize project highlights form with data from existing component or defaults
  useEffect(() => {
    // Skip if projectHighlightsComponent is still undefined (loading)
    if (projectHighlightsComponent === undefined) return;
    
    if (projectHighlightsComponent) {
      console.log('Setting project highlights content from existing component:', projectHighlightsComponent);
      
      setProjectHighlightsContent({
        features: projectHighlightsComponent.content.features || [],
        developer: projectHighlightsComponent.content.developer || {
          name: '',
          description: '',
          logo: '',
          videoUrl: ''
        }
      });
    } else {
      setProjectHighlightsContent({
        features: [],
        developer: {
          name: '',
          description: '',
          logo: '',
          videoUrl: ''
        }
      });
    }
  }, [projectHighlightsComponent, selectedLanguage]);

  // Initialize FAQ form with data from existing component or defaults
  useEffect(() => {
    // Skip if faqComponent is still undefined (loading)
    if (faqComponent === undefined) return;
    
    if (faqComponent) {
      console.log('Setting FAQ content from existing component:', faqComponent);
      
      setFaqContent({
        faqs: faqComponent.content.faqs || []
      });
    } else {
      setFaqContent({
        faqs: []
      });
    }
  }, [faqComponent, selectedLanguage]);

  // Initialize Location form with data from existing component or defaults
  useEffect(() => {
    // Skip if locationComponent is still undefined (loading)
    if (locationComponent === undefined) return;
    
    if (locationComponent) {
      console.log('Setting location content from existing component:', locationComponent);
      
      setLocationContent({
        projectName: locationComponent.content.projectName || '',
        coordinates: locationComponent.content.coordinates || { lat: 25.069750, lng: 55.247471 },
        nearbyPlaces: locationComponent.content.nearbyPlaces || [],
        keyDistances: locationComponent.content.keyDistances || []
      });
    } else {
      setLocationContent({
        projectName: '',
        coordinates: { lat: 25.069750, lng: 55.247471 },
        nearbyPlaces: [],
        keyDistances: []
      });
    }
  }, [locationComponent, selectedLanguage]);

  // Initialize Payment Plan form with data from existing component or defaults
  useEffect(() => {
    // Skip if paymentPlanComponent is still undefined (loading)
    if (paymentPlanComponent === undefined) return;
    
    if (paymentPlanComponent) {
      console.log('Setting payment plan content from existing component:', paymentPlanComponent);
      
      setPaymentPlanContent({
        title: paymentPlanComponent.content.title || '',
        description: paymentPlanComponent.content.description || '',
        plans: paymentPlanComponent.content.plans || []
      });
    } else {
      setPaymentPlanContent({
        title: '',
        description: '',
        plans: []
      });
    }
  }, [paymentPlanComponent, selectedLanguage]);

  // Initialize Gallery form with data from existing component or defaults
  useEffect(() => {
    // Skip if galleryComponent is still undefined (loading)
    if (galleryComponent === undefined) return;
    
    if (galleryComponent) {
      console.log('Setting gallery content from existing component:', galleryComponent);
      
      setGalleryContent({
        images: galleryComponent.content.images || []
      });
    } else {
      setGalleryContent({
        images: []
      });
    }
  }, [galleryComponent, selectedLanguage]);

  // Initialize Legal Info form with data from existing component or defaults
  useEffect(() => {
    // Skip if legalInfoComponent is still undefined (loading)
    if (legalInfoComponent === undefined) return;
    
    if (legalInfoComponent) {
      console.log('Setting legal info content from existing component:', legalInfoComponent);
      
      setLegalInfoContent({
        dldPermitNumber: legalInfoComponent.content.dldPermitNumber || '',
        qrCodeUrl: legalInfoComponent.content.qrCodeUrl || '',
        qrCodeFileId: legalInfoComponent.content.qrCodeFileId || undefined
      });
    } else {
      setLegalInfoContent({
        dldPermitNumber: '',
        qrCodeUrl: '',
        qrCodeFileId: undefined
      });
    }
  }, [legalInfoComponent, selectedLanguage]);

  // Initialize Lead Form with data from existing component or defaults
  useEffect(() => {
    // Skip if leadFormComponent is still undefined (loading)
    if (leadFormComponent === undefined) return;
    
    if (leadFormComponent) {
      console.log('Setting lead form content from existing component:', leadFormComponent);
      
      setLeadFormContent({
        title: leadFormComponent.content.title || '',
        description: leadFormComponent.content.description || '',
        stepTitles: leadFormComponent.content.stepTitles || {
          basicInfo: '',
          purpose: '',
          investment: '',
          endUser: '',
          contact: ''
        },
        stepDescriptions: leadFormComponent.content.stepDescriptions || {
          basicInfo: '',
          purpose: '',
          investment: '',
          endUser: '',
          contact: ''
        },
        purposeDescriptions: leadFormComponent.content.purposeDescriptions || {
          investment: '',
          endUser: ''
        },
        contactDescriptions: leadFormComponent.content.contactDescriptions || {
          whatsapp: '',
          phone: '',
          email: ''
        },
        labels: leadFormComponent.content.labels || {
          fullName: '',
          email: '',
          phone: '',
          whatsYourPurpose: '',
          investment: '',
          endUser: '',
          investmentPreferences: '',
          investmentBudget: '',
          investmentTimeline: '',
          yourDreamHome: '',
          preferredSize: '',
          budgetRange: '',
          howToContact: '',
          whatsapp: '',
          phoneCall: '',
          emailOption: '',
          continue: '',
          back: '',
          submit: '',
          submitting: '',
          thankYou: '',
          willContactYou: ''
        },
        budgetOptions: leadFormComponent.content.budgetOptions || [
          { value: '500k-1m', label: 'AED 500K - 1M' },
          { value: '1m-2m', label: 'AED 1M - 2M' },
          { value: '2m-5m', label: 'AED 2M - 5M' },
          { value: '5m+', label: 'AED 5M+' }
        ],
        timelineOptions: leadFormComponent.content.timelineOptions || [
          { value: 'immediate', label: 'Immediate' },
          { value: '3months', label: 'Within 3 months' },
          { value: '6months', label: 'Within 6 months' },
          { value: '1year', label: 'Within 1 year' }
        ],
        bedroomOptions: leadFormComponent.content.bedroomOptions || [
          { value: 'studio', label: 'Studio' },
          { value: '1bed', label: '1 Bedroom' },
          { value: '2bed', label: '2 Bedrooms' },
          { value: '3bed', label: '3 Bedrooms' },
          { value: '4bed+', label: '4+ Bedrooms' }
        ],
        validationMessages: leadFormComponent.content.validationMessages || {
          nameRequired: '',
          emailRequired: '',
          emailInvalid: '',
          phoneRequired: '',
          phoneInvalid: ''
        },
        fields: leadFormComponent.content.fields || {
          name: true,
          email: true,
          phone: true,
          purpose: true,
          budget: true,
          timeline: true,
          bedrooms: true,
          contactPreference: true
        },
        submitButtonText: leadFormComponent.content.submitButtonText || 'Submit',
        successMessage: leadFormComponent.content.successMessage || 'Thank you for your interest! We will contact you soon.',
        privacyText: leadFormComponent.content.privacyText || 'By submitting this form, you agree to our privacy policy and terms of service.',
        success: leadFormComponent.content.success || {
          title: 'Download Started!',
          description: 'Thank you for your interest. We\'ll review your submission and get back to you soon.'
        },
        error: leadFormComponent.content.error || {
          title: 'Submission Error',
          description: 'Failed to submit the form. Please try again later.'
        }
      });
    } else {
      setLeadFormContent(INITIAL_LEAD_FORM_CONTENT);
    }
  }, [leadFormComponent, selectedLanguage]);

  // Initialize Download Form with data from existing component or defaults
  useEffect(() => {
    // Skip if downloadFormComponent is still undefined (loading)
    if (downloadFormComponent === undefined) return;
    
    if (downloadFormComponent) {
      console.log('Setting download form content from existing component:', downloadFormComponent);
      
      setDownloadFormContent({
        title: downloadFormComponent.content.title || 'Get Personalized Property Details',
        subtitle: downloadFormComponent.content.subtitle || 'Speak with our property experts and get:',
        welcomeTitle: downloadFormComponent.content.welcomeTitle || 'Get Personalized Property Details',
        welcomeDescription: downloadFormComponent.content.welcomeDescription || 'Speak with our property experts and get:',
        benefits: downloadFormComponent.content.benefits || [
          'Detailed floor plans & pricing',
          'Virtual tour of the property',
          'Payment plan options',
          'Investment analysis report'
        ],
        stepTitles: downloadFormComponent.content.stepTitles || {
          basicInfo: '',
          contact: '',
          interest: '',
          experience: ''
        },
        stepDescriptions: downloadFormComponent.content.stepDescriptions || {
          basicInfo: '',
          contact: '',
          interest: '',
          experience: ''
        },
        labels: downloadFormComponent.content.labels || {
          firstName: 'First Name',
          lastName: 'Last Name',
          email: 'Email Address',
          phone: 'Phone Number',
          interest: 'What interests you most?',
          previousPurchases: 'Previous property purchases',
          investmentTimeline: 'Investment timeline',
          continue: 'Continue',
          back: 'Back',
          submit: 'Download Brochure',
          submitting: 'Submitting...',
          thankYou: 'Thank You!',
          willContactYou: 'Your brochure request has been received. We\'ll send it to your email shortly.'
        },
        validationMessages: downloadFormComponent.content.validationMessages || {
          firstNameRequired: '',
          lastNameRequired: '',
          emailRequired: '',
          emailInvalid: '',
          phoneRequired: ''
        },
        interestOptions: downloadFormComponent.content.interestOptions || [
          { value: 'investment', label: 'Investment opportunities' },
          { value: 'end-user', label: 'Personal residence' },
          { value: 'agent', label: 'Real estate agent' },
          { value: 'exploring', label: 'Just exploring' }
        ],
        previousPurchasesOptions: downloadFormComponent.content.previousPurchasesOptions || [
          { value: 'none', label: 'None' },
          { value: '1-2', label: '1-2 properties' },
          { value: '3-5', label: '3-5 properties' },
          { value: '5+', label: '5+ properties' }
        ],
        investmentTimelineOptions: downloadFormComponent.content.investmentTimelineOptions || [
          { value: 'immediate', label: 'Immediately' },
          { value: '3months', label: 'Within 3 months' },
          { value: '6months', label: 'Within 6 months' },
          { value: 'exploring', label: 'Just exploring' }
        ],
        success: downloadFormComponent.content.success || {
          title: 'Thank You!',
          description: 'Your brochure request has been received. We\'ll send it to your email shortly.'
        },
        error: downloadFormComponent.content.error || {
          title: 'Submission Error',
          description: 'Failed to submit the form. Please try again later.'
        },
        privacyText: downloadFormComponent.content.privacyText || 'By submitting this form, you agree to our privacy policy and terms of service.'
      });
    } else {
      setDownloadFormContent(INITIAL_DOWNLOAD_FORM_CONTENT);
    }
  }, [downloadFormComponent, selectedLanguage]);

  // State for Email Template content
  const [emailTemplateContent, setEmailTemplateContent] = useState<EmailTemplateContent>({
    subject: "Welcome to {{projectName}} - We'll be in touch soon!",
    htmlContent: `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Welcome to {{projectName}}</title>
</head>
<body>
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 30px; text-align: center;">
            <h1 style="margin: 0; font-size: 28px; font-weight: bold;">Welcome, {{name}}!</h1>
            <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">Thank you for your interest in {{projectName}}</p>
        </div>
        
        <div style="padding: 40px 30px;">
            <h2 style="color: #333; font-size: 24px; margin-bottom: 20px;">We're excited to help you find your perfect property!</h2>
            
            <p>Dear {{fullName}},</p>
            
            <p>Thank you for reaching out to us about <strong>{{projectName}}</strong>. We've received your inquiry and our property experts will contact you soon with personalized information tailored to your needs.</p>
            
            <div style="background: #f8f9ff; border-left: 4px solid #667eea; padding: 20px; margin: 25px 0; border-radius: 0 8px 8px 0;">
                <h3 style="margin-top: 0; color: #667eea;">What happens next?</h3>
                <ul style="margin-bottom: 0;">
                    <li>Our team will review your requirements within 2 hours</li>
                    <li>We'll prepare personalized property information for you</li>
                    <li>A property consultant will contact you within 24 hours</li>
                    <li>We'll schedule a convenient time to discuss your needs</li>
                </ul>
            </div>
            
            <p>In the meantime, feel free to explore more about <strong>{{projectName}}</strong> and discover why it's the perfect choice for your investment or dream home.</p>
            
            <div style="text-align: center; margin: 30px 0;">
                <a href="{{projectLink}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 16px 32px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px;">Explore {{projectName}} →</a>
            </div>
        </div>
        
        <div style="background: #f8f9fa; padding: 30px; text-align: center; border-top: 1px solid #e9ecef;">
            <p><strong>Best regards,</strong><br>The {{projectName}} Team</p>
            <p style="font-size: 12px; color: #999; margin-top: 20px;">
                This email was sent because you expressed interest in {{projectName}}. 
                <br>If you didn't request this information, please ignore this email.
            </p>
        </div>
    </div>
</body>
</html>`,
    textContent: `Welcome, {{name}}!

Thank you for your interest in {{projectName}}.

Dear {{fullName}},

Thank you for reaching out to us about {{projectName}}. We've received your inquiry and our property experts will contact you soon with personalized information tailored to your needs.

What happens next?
- Our team will review your requirements within 2 hours
- We'll prepare personalized property information for you
- A property consultant will contact you within 24 hours
- We'll schedule a convenient time to discuss your needs

In the meantime, feel free to explore more about {{projectName}} and discover why it's the perfect choice for your investment or dream home.

Explore {{projectName}}: {{projectLink}}

Best regards,
The {{projectName}} Team

This email was sent because you expressed interest in {{projectName}}. If you didn't request this information, please ignore this email.`,
    isActive: true
  });

  // Queries for email templates
  const emailTemplates = useAuthenticatedQuery(api.emails.getAllEmailTemplates, 
    projectId ? { projectId: projectId as Id<"projects">, type: "welcome" } : "skip"
  );
  
 
  
  // Queries for brochure email templates
  const brochureEmailTemplates = useAuthenticatedQuery(api.emails.getAllEmailTemplates, 
    projectId ? { projectId: projectId as Id<"projects">, type: "brochure" } : "skip"
  );

  // Query for brochure email template with fallback logic
  const brochureTemplateWithFallback = useAuthenticatedQuery(api.emails.getBrochureTemplateWithFallback, 
    projectId && selectedLanguage ? { 
      projectId: projectId as Id<"projects">, 
      language: selectedLanguage 
    } : "skip"
  );

  // Email template mutations
  const createEmailTemplate = useAuthenticatedMutation(api.emails.createEmailTemplate);
  const updateEmailTemplate = useAuthenticatedMutation(api.emails.updateEmailTemplate);

  // Load existing email template when language changes
  useEffect(() => {
    if (emailTemplates && selectedLanguage) {
      const existingTemplate = emailTemplates.find(
        template => template.projectId === projectId && 
        template.languageCode === selectedLanguage && 
        template.type === "welcome"
      );
      
      if (existingTemplate) {
        console.log('Loading existing email template:', existingTemplate);
        setEmailTemplateContent({
          subject: existingTemplate.subject,
          htmlContent: existingTemplate.htmlContent,
          textContent: existingTemplate.textContent || '',
          isActive: existingTemplate.isActive,
        });
      }
    }
  }, [emailTemplates, selectedLanguage, projectId]);
  
  // Load existing brochure email template when language changes
  useEffect(() => {
    if (brochureTemplateWithFallback && selectedLanguage) {
      const { template, fallbackUsed, fallbackType } = brochureTemplateWithFallback;
      
      if (template) {
        console.log('Loading brochure email template:', {
          template: template,
          fallbackUsed,
          fallbackType,
          language: selectedLanguage
        });
        
        setBrochureEmailTemplateContent({
          subject: template.subject,
          htmlContent: template.htmlContent,
          textContent: template.textContent || '',
          isActive: template.isActive,
        });

        // Show a helpful message about which fallback was used
        if (fallbackUsed) {
          let fallbackMessage = '';
          switch (fallbackType) {
            case 'latest_language':
              fallbackMessage = `Using latest ${selectedLanguage} template from another project`;
              break;
            case 'project_english':
              fallbackMessage = `Using English template from current project`;
              break;
            case 'latest_english':
              fallbackMessage = `Using latest English template from another project`;
              break;
          }
          console.log(`Brochure template fallback: ${fallbackMessage}`);
        }
      } else {
        // Reset to default when no template exists at all
        console.log('No brochure email template found, using default');
        setBrochureEmailTemplateContent({
          subject: "Your {{projectName}} Brochure is Ready!",
          htmlContent: `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Your {{projectName}} Brochure</title>
</head>
<body>
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 30px; text-align: center;">
            <h1 style="margin: 0; font-size: 28px; font-weight: bold;">Your {{projectName}} Brochure</h1>
            <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">Thank you for your interest in our project!</p>
        </div>
        
        <div style="padding: 40px 30px;">
            <h2 style="color: #333; font-size: 24px; margin-bottom: 20px;">We're excited to share our project with you!</h2>
            
            <p>Dear {{fullName}},</p>
            
            <p>Thank you for reaching out to us about <strong>{{projectName}}</strong>. We've received your inquiry and our property experts will contact you soon with personalized information tailored to your needs.</p>
            
            <div style="background: #f8f9ff; border-left: 4px solid #667eea; padding: 20px; margin: 25px 0; border-radius: 0 8px 8px 0;">
                <h3 style="margin-top: 0; color: #667eea;">What's included in the brochure?</h3>
                <ul style="margin-bottom: 0;">
                    <li>Detailed floor plans & pricing</li>
                    <li>Virtual tour of the property</li>
                    <li>Payment plan options</li>
                    <li>Investment analysis report</li>
                </ul>
            </div>
            
            <p>In the brochure, you'll find everything you need to know about <strong>{{projectName}}</strong> and why it's the perfect choice for your investment or dream home.</p>
            
            <div style="text-align: center; margin: 30px 0;">
                <a href="{{brochureLink}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 16px 32px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px;">Download Brochure →</a>
            </div>
        </div>
        
        <div style="background: #f8f9fa; padding: 30px; text-align: center; border-top: 1px solid #e9ecef;">
            <p><strong>Best regards,</strong><br>The {{projectName}} Team</p>
            <p style="font-size: 12px; color: #999; margin-top: 20px;">
                This email was sent because you expressed interest in {{projectName}}. 
                <br>If you didn't request this information, please ignore this email.
            </p>
        </div>
    </div>
</body>
</html>`,
          textContent: `Dear {{fullName}},

Thank you for your interest in our project!

We're excited to share our project with you. The brochure includes everything you need to know about {{projectName}} and why it's the perfect choice for your investment or dream home.

What's included in the brochure?
- Detailed floor plans & pricing
- Virtual tour of the property
- Payment plan options
- Investment analysis report

Download your brochure: {{brochureLink}}

Best regards,
The {{projectName}} Team

This email was sent because you expressed interest in {{projectName}}. If you didn't request this information, please ignore this email.`,
          isActive: true
        });
      }
    }
  }, [brochureTemplateWithFallback, selectedLanguage, projectId]);

  // State for Brochure Email Template content
  const [brochureEmailTemplateContent, setBrochureEmailTemplateContent] = useState<EmailTemplateContent>({
    subject: "Your {{projectName}} Brochure is Ready!",
    htmlContent: `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Your {{projectName}} Brochure</title>
</head>
<body>
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 40px 30px; text-align: center;">
            <h1 style="margin: 0; font-size: 28px; font-weight: bold;">Your {{projectName}} Brochure</h1>
            <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">Thank you for your interest in our project!</p>
        </div>
        
        <div style="padding: 40px 30px;">
            <h2 style="color: #333; font-size: 24px; margin-bottom: 20px;">We're excited to share our project with you!</h2>
            
            <p>Dear {{fullName}},</p>
            
            <p>Thank you for reaching out to us about <strong>{{projectName}}</strong>. We've received your inquiry and our property experts will contact you soon with personalized information tailored to your needs.</p>
            
            <div style="background: #f8f9ff; border-left: 4px solid #667eea; padding: 20px; margin: 25px 0; border-radius: 0 8px 8px 0;">
                <h3 style="margin-top: 0; color: #667eea;">What's included in the brochure?</h3>
                <ul style="margin-bottom: 0;">
                    <li>Detailed floor plans & pricing</li>
                    <li>Virtual tour of the property</li>
                    <li>Payment plan options</li>
                    <li>Investment analysis report</li>
                </ul>
            </div>
            
            <p>In the brochure, you'll find everything you need to know about <strong>{{projectName}}</strong> and why it's the perfect choice for your investment or dream home.</p>
            
            <div style="text-align: center; margin: 30px 0;">
                <a href="{{brochureLink}}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 16px 32px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px;">Download Brochure →</a>
            </div>
        </div>
        
        <div style="background: #f8f9fa; padding: 30px; text-align: center; border-top: 1px solid #e9ecef;">
            <p><strong>Best regards,</strong><br>The {{projectName}} Team</p>
            <p style="font-size: 12px; color: #999; margin-top: 20px;">
                This email was sent because you expressed interest in {{projectName}}. 
                <br>If you didn't request this information, please ignore this email.
            </p>
        </div>
    </div>
</body>
</html>`,
    textContent: `Dear {{fullName}},

Thank you for your interest in our project!

We're excited to share our project with you. The brochure includes everything you need to know about {{projectName}} and why it's the perfect choice for your investment or dream home.

What's included in the brochure?
- Detailed floor plans & pricing
- Virtual tour of the property
- Payment plan options
- Investment analysis report

Download your brochure: {{brochureLink}}

Best regards,
The {{projectName}} Team

This email was sent because you expressed interest in {{projectName}}. If you didn't request this information, please ignore this email.`,
    isActive: true
  });

  // State for brochure file management
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [brochureFileId, setBrochureFileId] = useState<string | null>(null);
  const [brochureFileName, setBrochureFileName] = useState<string>('');
  const [brochureUploadProgress, setBrochureUploadProgress] = useState<number>(0);
  const [isBrochureUploading, setIsBrochureUploading] = useState<boolean>(false);

  // Project Knowledge for AI
  const [projectKnowledge, setProjectKnowledge] = useState<string>('');

  // AI Generation Hook
  const { generate: generateAI, isGenerating, error: aiError } = useAIGeneration({
    onSuccess: (text) => {
      toast({
        title: 'Success',
        description: 'AI content generated successfully',
        variant: 'success'
      });
    },
    onError: (error) => {
      toast({
        title: 'AI Generation Error',
        description: error,
        variant: 'destructive'
      });
    }
  });

  if (!project) {
    return <div>Loading project...</div>;
  }

  const handleLanguageChange = (value: string) => {
    setSelectedLanguage(value);
    // Force refresh components query when language changes
    setQueryTimestamp(Date.now());
  };

  const addHighlight = () => {
    setHeroContent(prev => ({
      ...prev,
      highlights: [...prev.highlights, '']
    }));
  };

  const removeHighlight = (index: number) => {
    setHeroContent(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };

  const updateHighlight = (index: number, value: string) => {
    setHeroContent(prev => {
      const newHighlights = [...prev.highlights];
      newHighlights[index] = value;
      return {
        ...prev,
        highlights: newHighlights
      };
    });
  };

  const toggleDownloadForm = () => {
    setHeroContent(prev => ({
      ...prev,
      showDownloadForm: !prev.showDownloadForm
    }));
  };
  
  const handleBackgroundImageChange = (fileId: FileId | null, imageUrl: string) => {
    console.log('PageBuilder received image change:', { 
      fileId, 
      imageUrl,
      projectId: projectId?.toString(),
      componentId: heroComponent?._id?.toString()
    });
    
    if (!imageUrl) {
      console.log('No image URL provided, skipping update');
      return;
    }
    
    setHeroContent(prev => {
      // Check if anything has actually changed to prevent unnecessary updates
      if (prev.backgroundImage === imageUrl && prev.backgroundImageId === fileId) {
        return prev;
      }
      
      // Store the actual file ID - the URL is just for immediate preview
      // When the component loads again, it will fetch the proper URL from the server
      const updated = {
        ...prev,
        backgroundImage: imageUrl,
        backgroundImageId: fileId || undefined
      };
      
      console.log('Updated hero content with new background:', {
        backgroundImage: updated.backgroundImage,
        backgroundImageId: updated.backgroundImageId
      });
      
      return updated;
    });
  };

  const saveHeroComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    // Log detailed information before saving
    console.log('Saving hero component with data:', {
      projectId: projectId.toString(),
      languageCode: selectedLanguage,
      componentType: 'hero',
      componentId: heroComponent?._id?.toString(),
      backgroundImage: heroContent.backgroundImage ? 'present' : 'absent',
      backgroundImageId: heroContent.backgroundImageId,
      backgroundImageUrl: heroContent.backgroundImage,
      contentSize: JSON.stringify(heroContent).length
    });

    // Validate that we have the image data to save
    if (!heroContent.backgroundImage && heroComponent?.content?.backgroundImage) {
      console.log('Preserving existing backgroundImage from component');
      setHeroContent(prev => ({
        ...prev,
        backgroundImage: heroComponent.content.backgroundImage
      }));
    }

    if (!heroContent.backgroundImageId && heroComponent?.content?.backgroundImageId) {
      console.log('Preserving existing backgroundImageId from component');
      setHeroContent(prev => ({
        ...prev,
        backgroundImageId: heroComponent.content.backgroundImageId
      }));
    }

    setIsLoading(true);
    try {
      console.log('Calling upsertComponent mutation...');
      
      // Create a copy of the content to save, ensuring we have image data
      const contentToSave = {
        ...heroContent,
        backgroundImage: heroContent.backgroundImage || heroComponent?.content?.backgroundImage || '',
        backgroundImageId: heroContent.backgroundImageId || heroComponent?.content?.backgroundImageId
      };
      
      console.log('Final hero content being saved:', contentToSave);
      
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'hero',
        order: 100, // Hero is first with order 100
        content: contentToSave,
        isActive: true,
        componentId: heroComponent?._id
      });

      console.log('Component saved successfully with ID:', componentId);
      
      // Refresh components list without changing the active tab
      refreshComponents();
      
      toast({
        title: 'Success',
        description: 'Hero component saved successfully',
      });
    } catch (error) {
      console.error('Error saving hero component:', error);
      
      // Add detailed error information to help debug the issue
      const errorMessage = error instanceof Error ? error.message : 'Failed to save hero component';
      console.error('Error details:', {
        message: errorMessage,
        projectId: projectId.toString(),
        languageCode: selectedLanguage,
        heroComponentId: heroComponent?._id?.toString()
      });
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Save project overview component
  const saveProjectOverviewComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving project overview component with data:', projectOverviewContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'projectOverview',
        order: 300, // Third component
        content: projectOverviewContent,
        isActive: true,
        componentId: projectOverviewComponent?._id
      });
      
      console.log('Project overview component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Project overview section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving project overview component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save project overview'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add a new selling point
  const addSellingPoint = () => {
    setProjectOverviewContent(prev => ({
      ...prev,
      sellingPoints: [
        ...prev.sellingPoints, 
        { 
          icon: 'location', 
          title: '', 
          description: '' 
        }
      ]
    }));
  };

  // Remove a selling point
  const removeSellingPoint = (index: number) => {
    setProjectOverviewContent(prev => ({
      ...prev,
      sellingPoints: prev.sellingPoints.filter((_, i) => i !== index)
    }));
  };

  // Update a selling point
  const updateSellingPoint = (index: number, field: keyof SellingPoint, value: string) => {
    setProjectOverviewContent(prev => {
      const newSellingPoints = [...prev.sellingPoints];
      newSellingPoints[index] = {
        ...newSellingPoints[index],
        [field]: value
      };
      return {
        ...prev,
        sellingPoints: newSellingPoints
      };
    });
  };
  
  // Add a new unit type
  const addUnitType = () => {
    setProjectOverviewContent(prev => ({
      ...prev,
      unitTypes: [
        ...prev.unitTypes, 
        { 
          type: '', 
          size: '', 
          startingPrice: '',
          image: ''
        }
      ]
    }));
  };

  // Remove a unit type
  const removeUnitType = (index: number) => {
    setProjectOverviewContent(prev => ({
      ...prev,
      unitTypes: prev.unitTypes.filter((_, i) => i !== index)
    }));
  };

  // Update a unit type
  const updateUnitType = (index: number, field: keyof UnitType, value: string) => {
    setProjectOverviewContent(prev => {
      const newUnitTypes = [...prev.unitTypes];
      newUnitTypes[index] = {
        ...newUnitTypes[index],
        [field]: value
      };
      return {
        ...prev,
        unitTypes: newUnitTypes
      };
    });
  };

  // Handle unit type image change
  const handleUnitTypeImageChange = (index: number, fileId: FileId | null, imageUrl: string) => {
    setProjectOverviewContent(prev => {
      const newUnitTypes = [...prev.unitTypes];
      newUnitTypes[index] = {
        ...newUnitTypes[index],
        image: imageUrl,
        imageId: fileId || undefined
      };
      return {
        ...prev,
        unitTypes: newUnitTypes
      };
    });
  };

  // Handle QR code file upload for legal info
  const handleQrCodeFileChange = (fileId: FileId | null, imageUrl: string) => {
    setLegalInfoContent(prev => ({
      ...prev,
      qrCodeUrl: imageUrl,
      qrCodeFileId: fileId || undefined
    }));
  };

  // Save project highlights component
  const saveProjectHighlightsComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving project highlights component with data:', projectHighlightsContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'projectHighlights',
        order: 200, // Second component after hero
        content: projectHighlightsContent,
        isActive: true,
        componentId: projectHighlightsComponent?._id
      });
      
      console.log('Project highlights component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Project highlights section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving project highlights component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save project highlights'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add a new feature
  const addFeature = () => {
    setProjectHighlightsContent(prev => ({
      ...prev,
      features: [
        ...prev.features, 
        { 
          icon: 'location', 
          title: '', 
          description: '' 
        }
      ]
    }));
  };

  // Remove a feature
  const removeFeature = (index: number) => {
    setProjectHighlightsContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Update a feature
  const updateFeature = (index: number, field: keyof Feature, value: string) => {
    setProjectHighlightsContent(prev => {
      const newFeatures = [...prev.features];
      newFeatures[index] = {
        ...newFeatures[index],
        [field]: value
      };
      return {
        ...prev,
        features: newFeatures
      };
    });
  };
  
  // Update developer info
  const updateDeveloper = (field: keyof Developer, value: string) => {
    setProjectHighlightsContent(prev => ({
      ...prev,
      developer: {
        ...prev.developer!,
        [field]: value
      }
    }));
  };

  // Save FAQ component
  const saveFaqComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving FAQ component with data:', faqContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'faq',
        order: 700, // Seventh component
        content: faqContent,
        isActive: true,
        componentId: faqComponent?._id
      });
      
      console.log('FAQ component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'FAQ section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving FAQ component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save FAQ'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add a new FAQ item
  const addFaqItem = () => {
    setFaqContent(prev => ({
      ...prev,
      faqs: [
        ...prev.faqs, 
        { 
          id: crypto.randomUUID(), 
          question: '', 
          answer: '' 
        }
      ]
    }));
  };

  // Remove a FAQ item
  const removeFaqItem = (id: string) => {
    setFaqContent(prev => ({
      ...prev,
      faqs: prev.faqs.filter(item => item.id !== id)
    }));
  };

  // Update a FAQ item
  const updateFaqItem = (id: string, field: 'question' | 'answer', value: string) => {
    setFaqContent(prev => {
      const newFaqs = prev.faqs.map(item => {
        if (item.id === id) {
          return {
            ...item,
            [field]: value
          };
        }
        return item;
      });
      return {
        ...prev,
        faqs: newFaqs
      };
    });
  };

  // Save Location component
  const saveLocationComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving location component with data:', locationContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'location',
        order: 600, // Sixth component
        content: locationContent,
        isActive: true,
        componentId: locationComponent?._id
      });
      
      console.log('Location component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Location section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving location component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save location'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add a nearby place
  const addNearbyPlace = () => {
    setLocationContent(prev => ({
      ...prev,
      nearbyPlaces: [
        ...prev.nearbyPlaces, 
        { 
          category: 'lifestyle', 
          name: '', 
          distance: '', 
          time: '' 
        }
      ]
    }));
  };

  // Remove a nearby place
  const removeNearbyPlace = (index: number) => {
    setLocationContent(prev => ({
      ...prev,
      nearbyPlaces: prev.nearbyPlaces.filter((_, i) => i !== index)
    }));
  };

  // Update a nearby place
  const updateNearbyPlace = (index: number, field: keyof LocationContent['nearbyPlaces'][0], value: string) => {
    setLocationContent(prev => {
      const newPlaces = [...prev.nearbyPlaces];
      newPlaces[index] = {
        ...newPlaces[index],
        [field]: value as any // Need to cast because TypeScript doesn't know category is a specific union type
      };
      return {
        ...prev,
        nearbyPlaces: newPlaces
      };
    });
  };
  
  // Add a key distance
  const addKeyDistance = () => {
    setLocationContent(prev => ({
      ...prev,
      keyDistances: [
        ...prev.keyDistances,
        {
          place: '',
          time: ''
        }
      ]
    }));
  };
  
  // Remove a key distance
  const removeKeyDistance = (index: number) => {
    setLocationContent(prev => ({
      ...prev,
      keyDistances: prev.keyDistances.filter((_, i) => i !== index)
    }));
  };
  
  // Update a key distance
  const updateKeyDistance = (index: number, field: keyof LocationContent['keyDistances'][0], value: string) => {
    setLocationContent(prev => {
      const newDistances = [...prev.keyDistances];
      newDistances[index] = {
        ...newDistances[index],
        [field]: value
      };
      return {
        ...prev,
        keyDistances: newDistances
      };
    });
  };

  // Save Payment Plan component
  const savePaymentPlanComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving payment plan component with data:', paymentPlanContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'paymentPlan',
        order: 500, // Fifth component
        content: paymentPlanContent,
        isActive: true,
        componentId: paymentPlanComponent?._id
      });
      
      console.log('Payment plan component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Payment plan section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving payment plan component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save payment plan'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add a payment plan
  const addPaymentPlan = () => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: [
        ...prev.plans,
        {
          id: crypto.randomUUID(),
          name: '',
          description: '',
          downPayment: {
            percentage: 20,
            dueDate: 'On Booking'
          },
          milestones: [],
          highlights: []
        }
      ]
    }));
  };

  // Remove a payment plan
  const removePaymentPlan = (id: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.filter(plan => plan.id !== id)
    }));
  };

  // Update a payment plan's basic info
  const updatePaymentPlan = (id: string, field: 'name' | 'description', value: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === id) {
          return {
            ...plan,
            [field]: value
          };
        }
        return plan;
      })
    }));
  };

  // Update down payment details
  const updateDownPayment = (planId: string, field: 'percentage' | 'dueDate', value: any) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            downPayment: {
              ...plan.downPayment,
              [field]: field === 'percentage' ? parseFloat(value) : value
            }
          };
        }
        return plan;
      })
    }));
  };

  // Add a milestone to a payment plan
  const addMilestone = (planId: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            milestones: [
              ...plan.milestones,
              {
                percentage: 10,
                milestone: '',
                dueDate: '',
                description: ''
              }
            ]
          };
        }
        return plan;
      })
    }));
  };

  // Remove a milestone
  const removeMilestone = (planId: string, index: number) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            milestones: plan.milestones.filter((_, i) => i !== index)
          };
        }
        return plan;
      })
    }));
  };

  // Update a milestone
  const updateMilestone = (planId: string, index: number, field: keyof PaymentPlanContent['plans'][0]['milestones'][0], value: any) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          const newMilestones = [...plan.milestones];
          newMilestones[index] = {
            ...newMilestones[index],
            [field]: field === 'percentage' ? parseFloat(value) : value
          };
          return {
            ...plan,
            milestones: newMilestones
          };
        }
        return plan;
      })
    }));
  };

  // Add a highlight to a payment plan
  const addHighlightToPaymentPlan = (planId: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            highlights: [
              ...plan.highlights,
              {
                icon: 'calendar',
                title: '',
                description: ''
              }
            ]
          };
        }
        return plan;
      })
    }));
  };

  // Remove a highlight
  const removeHighlightFromPaymentPlan = (planId: string, index: number) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            highlights: plan.highlights.filter((_, i) => i !== index)
          };
        }
        return plan;
      })
    }));
  };

  // Update a highlight
  const updatePaymentPlanHighlight = (planId: string, index: number, field: keyof PaymentPlanContent['plans'][0]['highlights'][0], value: string) => {
    setPaymentPlanContent(prev => ({
      ...prev,
      plans: prev.plans.map(plan => {
        if (plan.id === planId) {
          const newHighlights = [...plan.highlights];
          newHighlights[index] = {
            ...newHighlights[index],
            [field]: value
          };
          return {
            ...plan,
            highlights: newHighlights
          };
        }
        return plan;
      })
    }));
  };

  // Save Project Gallery component
  const saveGalleryComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving gallery component with data:', galleryContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'projectGallery',
        order: 400, // Fourth component
        content: galleryContent,
        isActive: true,
        componentId: galleryComponent?._id
      });
      
      console.log('Gallery component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Gallery section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving gallery component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save gallery'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add a gallery image
  const addGalleryImage = () => {
    setGalleryContent(prev => ({
      ...prev,
      images: [
        ...prev.images,
        {
          url: '',
          alt: '',
          type: 'horizontal',
          category: 'exterior'
        }
      ]
    }));
  };

  // Remove a gallery image
  const removeGalleryImage = (index: number) => {
    setGalleryContent(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Update a gallery image
  const updateGalleryImage = (index: number, field: keyof GalleryContent['images'][0], value: string) => {
    setGalleryContent(prev => {
      const newImages = [...prev.images];
      newImages[index] = {
        ...newImages[index],
        [field]: value as any // Type casting needed because TypeScript doesn't know about the specific union types
      };
      return {
        ...prev,
        images: newImages
      };
    });
  };

  // Save Legal Info component
  const saveLegalInfoComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving legal info component with data:', legalInfoContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'legalInfo',
        order: 800, // Last component
        content: legalInfoContent,
        isActive: true,
        componentId: legalInfoComponent?._id
      });
      
      console.log('Legal info component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Legal information section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving legal info component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save legal info'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save Lead Form component
  const saveLeadFormComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving lead form component with data:', leadFormContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'leadForm',
        order: 650, // Between location (600) and FAQ (700)
        content: leadFormContent,
        isActive: true,
        componentId: leadFormComponent?._id
      });
      
      console.log('Lead form component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Lead form section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving lead form component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save lead form'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save Download Form component
  const saveDownloadFormComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving download form component with data:', downloadFormContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'downloadForm',
        order: 625, // Between location (600) and lead form (650)
        content: downloadFormContent,
        isActive: true,
        componentId: downloadFormComponent?._id
      });
      
      console.log('Download form component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Download form section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving download form component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save download form'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Download form helper functions
  const addBenefit = () => {
    setDownloadFormContent(prev => ({
      ...prev,
      benefits: [...prev.benefits, '']
    }));
  };

  const removeBenefit = (index: number) => {
    setDownloadFormContent(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const updateBenefit = (index: number, value: string) => {
    setDownloadFormContent(prev => {
      const newBenefits = [...prev.benefits];
      newBenefits[index] = value;
      return {
        ...prev,
        benefits: newBenefits
      };
    });
  };

  const addInterestOption = () => {
    setDownloadFormContent(prev => ({
      ...prev,
      interestOptions: [...prev.interestOptions, { value: '', label: '' }]
    }));
  };

  const removeInterestOption = (index: number) => {
    setDownloadFormContent(prev => ({
      ...prev,
      interestOptions: prev.interestOptions.filter((_, i) => i !== index)
    }));
  };

  const updateInterestOption = (index: number, field: 'value' | 'label', value: string) => {
    setDownloadFormContent(prev => {
      const newOptions = [...prev.interestOptions];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return {
        ...prev,
        interestOptions: newOptions
      };
    });
  };

  const addPreviousPurchasesOption = () => {
    setDownloadFormContent(prev => ({
      ...prev,
      previousPurchasesOptions: [...prev.previousPurchasesOptions, { value: '', label: '' }]
    }));
  };

  const removePreviousPurchasesOption = (index: number) => {
    setDownloadFormContent(prev => ({
      ...prev,
      previousPurchasesOptions: prev.previousPurchasesOptions.filter((_, i) => i !== index)
    }));
  };

  const updatePreviousPurchasesOption = (index: number, field: 'value' | 'label', value: string) => {
    setDownloadFormContent(prev => {
      const newOptions = [...prev.previousPurchasesOptions];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return {
        ...prev,
        previousPurchasesOptions: newOptions
      };
    });
  };

  const addInvestmentTimelineOption = () => {
    setDownloadFormContent(prev => ({
      ...prev,
      investmentTimelineOptions: [...prev.investmentTimelineOptions, { value: '', label: '' }]
    }));
  };

  const removeInvestmentTimelineOption = (index: number) => {
    setDownloadFormContent(prev => ({
      ...prev,
      investmentTimelineOptions: prev.investmentTimelineOptions.filter((_, i) => i !== index)
    }));
  };

  const updateInvestmentTimelineOption = (index: number, field: 'value' | 'label', value: string) => {
    setDownloadFormContent(prev => {
      const newOptions = [...prev.investmentTimelineOptions];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return {
        ...prev,
        investmentTimelineOptions: newOptions
      };
    });
  };

  // Lead form helper functions for budget, timeline, and bedroom options
  const addBudgetOption = () => {
    setLeadFormContent(prev => ({
      ...prev,
      budgetOptions: [...prev.budgetOptions, { value: '', label: '' }]
    }));
  };

  const removeBudgetOption = (index: number) => {
    setLeadFormContent(prev => ({
      ...prev,
      budgetOptions: prev.budgetOptions.filter((_, i) => i !== index)
    }));
  };

  const updateBudgetOption = (index: number, field: 'value' | 'label', value: string) => {
    setLeadFormContent(prev => {
      const newOptions = [...prev.budgetOptions];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return {
        ...prev,
        budgetOptions: newOptions
      };
    });
  };

  const addTimelineOption = () => {
    setLeadFormContent(prev => ({
      ...prev,
      timelineOptions: [...prev.timelineOptions, { value: '', label: '' }]
    }));
  };

  const removeTimelineOption = (index: number) => {
    setLeadFormContent(prev => ({
      ...prev,
      timelineOptions: prev.timelineOptions.filter((_, i) => i !== index)
    }));
  };

  const updateTimelineOption = (index: number, field: 'value' | 'label', value: string) => {
    setLeadFormContent(prev => {
      const newOptions = [...prev.timelineOptions];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return {
        ...prev,
        timelineOptions: newOptions
      };
    });
  };

  const addBedroomOption = () => {
    setLeadFormContent(prev => ({
      ...prev,
      bedroomOptions: [...prev.bedroomOptions, { value: '', label: '' }]
    }));
  };

  const removeBedroomOption = (index: number) => {
    setLeadFormContent(prev => ({
      ...prev,
      bedroomOptions: prev.bedroomOptions.filter((_, i) => i !== index)
    }));
  };

  const updateBedroomOption = (index: number, field: 'value' | 'label', value: string) => {
    setLeadFormContent(prev => {
      const newOptions = [...prev.bedroomOptions];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return {
        ...prev,
        bedroomOptions: newOptions
      };
    });
  };

  // Reset functions for forms
  const resetDownloadFormToDefault = () => {
    setDownloadFormContent(DEFAULT_DOWNLOAD_FORM_CONTENT);
  };

  const resetLeadFormToDefault = () => {
    setLeadFormContent(DEFAULT_LEAD_FORM_CONTENT);
  };

  // Save Email Template
  const saveEmailTemplate = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving email template with data:', emailTemplateContent);
    setIsLoading(true);
    
    try {
      // Check if template already exists
      const existingTemplate = emailTemplates?.find(
        template => template.projectId === projectId && 
        template.languageCode === selectedLanguage && 
        template.type === "welcome"
      );

      if (existingTemplate) {
        // Update existing template
        await updateEmailTemplate({
          templateId: existingTemplate._id,
          subject: emailTemplateContent.subject,
          htmlContent: emailTemplateContent.htmlContent,
          textContent: emailTemplateContent.textContent,
          isActive: emailTemplateContent.isActive,
        });
      } else {
        // Create new template
        await createEmailTemplate({
          projectId: projectId as Id<"projects">,
          languageCode: selectedLanguage,
          type: "welcome",
          name: `welcome_${projectId}_${selectedLanguage}`,
          subject: emailTemplateContent.subject,
          htmlContent: emailTemplateContent.htmlContent,
          textContent: emailTemplateContent.textContent,
          isActive: emailTemplateContent.isActive,
        });
      }
      
      console.log('Email template saved successfully');
      toast({
        title: 'Success',
        description: 'Email template saved successfully'
      });
      
    } catch (error) {
      console.error('Error saving email template:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save email template'
      });
    } finally {
      setIsLoading(false);
    }
  };

 
  // Save Brochure Email Template
  const saveBrochureEmailTemplate = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving brochure email template with data:', brochureEmailTemplateContent);
    setIsLoading(true);
    
    try {
      // Check if template already exists
      const existingTemplate = brochureEmailTemplates?.find(
        template => template.projectId === projectId && 
        template.languageCode === selectedLanguage && 
        template.type === "brochure"
      );

      if (existingTemplate) {
        // Update existing template
        await updateEmailTemplate({
          templateId: existingTemplate._id,
          subject: brochureEmailTemplateContent.subject,
          htmlContent: brochureEmailTemplateContent.htmlContent,
          textContent: brochureEmailTemplateContent.textContent,
          isActive: brochureEmailTemplateContent.isActive,
        });
      } else {
        // Create new template
        await createEmailTemplate({
          projectId: projectId as Id<"projects">,
          languageCode: selectedLanguage,
          type: "brochure",
          name: `brochure_${projectId}_${selectedLanguage}`,
          subject: brochureEmailTemplateContent.subject,
          htmlContent: brochureEmailTemplateContent.htmlContent,
          textContent: brochureEmailTemplateContent.textContent,
          isActive: brochureEmailTemplateContent.isActive,
        });
      }
      
      console.log('Brochure email template saved successfully');
      toast({
        title: 'Success',
        description: 'Brochure email template saved successfully'
      });
      
    } catch (error) {
      console.error('Error saving brochure email template:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save brochure email template'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{project.name} - Page Builder</h2>
          <p className="text-gray-500">Configure your landing page components</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/projects')}>
            Back to Projects
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Language Selection</CardTitle>
          <CardDescription>
            Select the language you want to edit content for
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-4">
            <div className="flex items-center gap-4">
              <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-full md:w-1/3">
                  <SelectValue placeholder="Select a language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                      {language.name} {language.isDefault && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* RTL Mode Indicator */}
              {selectedLanguage && (
                <RTLIndicator language={selectedLanguage} />
              )}
            </div>
            
            {/* Translation Button */}
            {selectedLanguage && selectedLanguage !== 'en' && (
              <div className="flex items-center space-x-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Languages className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    Auto-translate from English
                  </p>
                  <p className="text-xs text-blue-700">
                    Translate all English content excluding lead and download forms (buttons, FAQs, email templates, etc.)
                  </p>
                </div>
                <Button
                  onClick={() => handleTranslateToLanguage(selectedLanguage)}
                  disabled={isTranslating}
                  size="sm"
                  className="whitespace-nowrap"
                >
                  {isTranslating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Translating...
                    </>
                  ) : (
                    <>
                      <Languages className="w-4 h-4 mr-2" />
                      Translate
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* Translation Error Display */}
            {translationError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{translationError}</p>
                <Button
                  onClick={resetTranslation}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  Dismiss
                </Button>
              </div>
            )}
            
            {/* Translation Success Display */}
            {translationResult && translationResult.success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  {translationResult.message}
                </p>
                {translationResult.errors.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-green-700 cursor-pointer">
                      View warnings ({translationResult.errors.length})
                    </summary>
                    <ul className="mt-1 text-xs text-green-600 list-disc list-inside">
                      {translationResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </details>
                )}
                <Button
                  onClick={resetTranslation}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  Dismiss
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedLanguage && (
        <div className="flex flex-col md:flex-row gap-6">
          <div className={`${showPreview ? 'w-1/2' : 'w-full'}`}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6 flex flex-wrap">
                <TabsTrigger value="hero">Hero Section</TabsTrigger>
                <TabsTrigger value="highlights">Project Highlights</TabsTrigger>
                <TabsTrigger value="overview">Project Overview</TabsTrigger>
                <TabsTrigger value="gallery">Gallery</TabsTrigger>
                <TabsTrigger value="payment">Payment Plan</TabsTrigger>
                <TabsTrigger value="location">Location</TabsTrigger>
                <TabsTrigger value="navigation">Navigation Settings</TabsTrigger>
                <TabsTrigger value="downloadForm">Download Form</TabsTrigger>
                <TabsTrigger value="leadForm">Lead Form</TabsTrigger>
                <TabsTrigger value="faq">FAQ</TabsTrigger>
                <TabsTrigger value="legal">Legal Info</TabsTrigger>
              </TabsList>

              <TabsContent value="hero" className="space-y-6">
                <HeroSection
                          projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  heroComponent={heroComponent}
                  heroContent={heroContent}
                  setHeroContent={setHeroContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
              
              <TabsContent value="highlights" className="space-y-6">
                <ProjectHighlightsSection
                  projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  projectHighlightsComponent={projectHighlightsComponent}
                  projectHighlightsContent={projectHighlightsContent}
                  setProjectHighlightsContent={setProjectHighlightsContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
              
              <TabsContent value="overview" className="space-y-6">
                <ProjectOverviewSection
                  projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  projectOverviewComponent={projectOverviewComponent}
                  projectOverviewContent={projectOverviewContent}
                  setProjectOverviewContent={setProjectOverviewContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
              
              <TabsContent value="gallery" className="space-y-6">
                <GallerySection
                  projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  galleryComponent={galleryComponent}
                  galleryContent={galleryContent}
                  setGalleryContent={setGalleryContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
              
              <TabsContent value="payment" className="space-y-6">
                <PaymentPlanSection
                  projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  paymentPlanComponent={paymentPlanComponent}
                  paymentPlanContent={paymentPlanContent}
                  setPaymentPlanContent={setPaymentPlanContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
              
              <TabsContent value="location" className="space-y-6">
                <LocationSection
                  projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  locationComponent={locationComponent}
                  locationContent={locationContent}
                  setLocationContent={setLocationContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
              
              <TabsContent value="navigation" className="space-y-6">
                <NavigationSettings
                  projectId={projectId as Id<"projects">}
                  languageCode={selectedLanguage}
                />
              </TabsContent>
              
              <TabsContent value="downloadForm" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Download Form</CardTitle>
                    <CardDescription>
                      Configure the download form that appears when users click the primary button. All text fields support multilingual content.
                    </CardDescription>
                    {selectedLanguage !== 'en' && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-900">
                              Manual Translation Required
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                              Forms are excluded from auto-translation. Please manually translate all form content for this language.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Basic Form Settings */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Basic Settings</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                          <Label htmlFor="downloadform-title">Form Title</Label>
                        <RTLInput
                            id="downloadform-title"
                            language={selectedLanguage}
                            value={downloadFormContent.title}
                            onChange={(e) => setDownloadFormContent(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Download Investment Brochure"
                        />
                      </div>
                      
                      <div className="space-y-2">
                          <Label htmlFor="download-subtitle">Subtitle</Label>
                        <RTLTextarea
                            id="download-subtitle"
                            language={selectedLanguage}
                            value={downloadFormContent.subtitle}
                            onChange={(e) => setDownloadFormContent(prev => ({ ...prev, subtitle: e.target.value }))}
                            placeholder="Get exclusive property details and investment insights"
                            rows={2}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="download-welcome-title">Welcome Title</Label>
                        <RTLInput
                          id="download-welcome-title"
                          language={selectedLanguage}
                          value={downloadFormContent.welcomeTitle}
                          onChange={(e) => setDownloadFormContent(prev => ({ ...prev, welcomeTitle: e.target.value }))}
                          placeholder="Get Personalized Property Details"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="download-welcome-description">Welcome Description</Label>
                        <RTLTextarea
                          id="download-welcome-description"
                          language={selectedLanguage}
                          value={downloadFormContent.welcomeDescription}
                          onChange={(e) => setDownloadFormContent(prev => ({ ...prev, welcomeDescription: e.target.value }))}
                          placeholder="Speak with our property experts and get:"
                          rows={2}
                        />
                      </div>
                      </div>
                      
                    {/* Benefits Section */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium border-b pb-2">Benefits</h3>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={addBenefit}
                          type="button"
                        >
                          Add Benefit
                        </Button>
                      </div>
                      
                      {downloadFormContent.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <RTLInput
                            language={selectedLanguage}
                            value={benefit}
                            onChange={(e) => updateBenefit(index, e.target.value)}
                            placeholder={`Benefit ${index + 1}`}
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeBenefit(index)}
                            className="text-red-500"
                          >
                            Remove
                          </Button>
                      </div>
                      ))}
                    </div>
                    
                    {/* Step Titles and Descriptions */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Step Titles & Descriptions</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="download-step-title-basic">Basic Info Step Title</Label>
                          <RTLInput
                            id="download-step-title-basic"
                            language={selectedLanguage}
                            value={downloadFormContent.stepTitles.basicInfo}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              stepTitles: { ...prev.stepTitles, basicInfo: e.target.value }
                            }))}
                            placeholder="Personal Information"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-step-desc-basic">Basic Info Step Description</Label>
                          <RTLInput
                            id="download-step-desc-basic"
                            language={selectedLanguage}
                            value={downloadFormContent.stepDescriptions.basicInfo}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                                ...prev,
                              stepDescriptions: { ...prev.stepDescriptions, basicInfo: e.target.value }
                            }))}
                            placeholder="Let's start with your basic details"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-step-title-contact">Contact Step Title</Label>
                          <RTLInput
                            id="download-step-title-contact"
                            language={selectedLanguage}
                            value={downloadFormContent.stepTitles.contact}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                                ...prev,
                              stepTitles: { ...prev.stepTitles, contact: e.target.value }
                            }))}
                            placeholder="Contact Information"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-step-desc-contact">Contact Step Description</Label>
                          <RTLInput
                            id="download-step-desc-contact"
                            language={selectedLanguage}
                            value={downloadFormContent.stepDescriptions.contact}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                                ...prev,
                              stepDescriptions: { ...prev.stepDescriptions, contact: e.target.value }
                            }))}
                            placeholder="How can we reach you?"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-step-title-interest">Interest Step Title</Label>
                          <Input
                            id="download-step-title-interest"
                            value={downloadFormContent.stepTitles.interest}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                                ...prev,
                              stepTitles: { ...prev.stepTitles, interest: e.target.value }
                            }))}
                            placeholder="Your Interest"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-step-desc-interest">Interest Step Description</Label>
                          <Input
                            id="download-step-desc-interest"
                            value={downloadFormContent.stepDescriptions.interest}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                                ...prev,
                              stepDescriptions: { ...prev.stepDescriptions, interest: e.target.value }
                            }))}
                            placeholder="What interests you most about this property?"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-step-title-experience">Experience Step Title</Label>
                          <Input
                            id="download-step-title-experience"
                            value={downloadFormContent.stepTitles.experience}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                                ...prev,
                              stepTitles: { ...prev.stepTitles, experience: e.target.value }
                            }))}
                            placeholder="Your Experience"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-step-desc-experience">Experience Step Description</Label>
                          <Input
                            id="download-step-desc-experience"
                            value={downloadFormContent.stepDescriptions.experience}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                                ...prev,
                              stepDescriptions: { ...prev.stepDescriptions, experience: e.target.value }
                            }))}
                            placeholder="Help us understand your background"
                          />
                        </div>
                      </div>
                        </div>
                        
                    {/* Interest Options */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium border-b pb-2">Interest Options</h3>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={addInterestOption}
                          type="button"
                        >
                          Add Interest Option
                        </Button>
                      </div>
                      
                      {downloadFormContent.interestOptions.map((option, index) => (
                        <div key={index} className="grid grid-cols-2 gap-2">
                          <Input
                            value={option.value}
                            onChange={(e) => updateInterestOption(index, 'value', e.target.value)}
                            placeholder="Value (e.g., investment)"
                          />
                        <div className="flex items-center space-x-2">
                            <Input
                              value={option.label}
                              onChange={(e) => updateInterestOption(index, 'label', e.target.value)}
                              placeholder="Label (e.g., Investment Opportunity)"
                            />
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeInterestOption(index)}
                              className="text-red-500"
                            >
                              Remove
                            </Button>
                        </div>
                      </div>
                      ))}
                    </div>
                    
                    {/* Previous Purchases Options */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium border-b pb-2">Previous Purchases Options</h3>
                    <Button
                      variant="outline"
                          size="sm" 
                          onClick={addPreviousPurchasesOption}
                          type="button"
                        >
                          Add Previous Purchase Option
                    </Button>
                      </div>
                      
                      {downloadFormContent.previousPurchasesOptions.map((option, index) => (
                        <div key={index} className="grid grid-cols-2 gap-2">
                          <Input
                            value={option.value}
                            onChange={(e) => updatePreviousPurchasesOption(index, 'value', e.target.value)}
                            placeholder="Value (e.g., first-time)"
                          />
                          <div className="flex items-center space-x-2">
                            <Input
                              value={option.label}
                              onChange={(e) => updatePreviousPurchasesOption(index, 'label', e.target.value)}
                              placeholder="Label (e.g., First-time buyer)"
                            />
                    <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removePreviousPurchasesOption(index)}
                              className="text-red-500"
                            >
                              Remove
                    </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Investment Timeline Options */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium border-b pb-2">Investment Timeline Options</h3>
                    <Button 
                      variant="outline" 
                          size="sm" 
                          onClick={addInvestmentTimelineOption}
                          type="button"
                    >
                          Add Timeline Option
                    </Button>
                      </div>
                      
                      {downloadFormContent.investmentTimelineOptions.map((option, index) => (
                        <div key={index} className="grid grid-cols-2 gap-2">
                          <Input
                            value={option.value}
                            onChange={(e) => updateInvestmentTimelineOption(index, 'value', e.target.value)}
                            placeholder="Value (e.g., immediate)"
                          />
                          <div className="flex items-center space-x-2">
                            <Input
                              value={option.label}
                              onChange={(e) => updateInvestmentTimelineOption(index, 'label', e.target.value)}
                              placeholder="Label (e.g., Immediate)"
                            />
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeInvestmentTimelineOption(index)}
                              className="text-red-500"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                          </div>
                          
                    {/* Validation Messages */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Validation Messages</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="download-validation-firstname">First Name Required</Label>
                              <Input
                            id="download-validation-firstname"
                            value={downloadFormContent.validationMessages.firstNameRequired}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, firstNameRequired: e.target.value }
                            }))}
                            placeholder="First name is required"
                              />
                            </div>
                            
                        <div className="space-y-2">
                          <Label htmlFor="download-validation-lastname">Last Name Required</Label>
                          <Input
                            id="download-validation-lastname"
                            value={downloadFormContent.validationMessages.lastNameRequired}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, lastNameRequired: e.target.value }
                            }))}
                            placeholder="Last name is required"
                              />
                            </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-validation-email">Email Required</Label>
                          <Input
                            id="download-validation-email"
                            value={downloadFormContent.validationMessages.emailRequired}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, emailRequired: e.target.value }
                            }))}
                            placeholder="Email is required"
                          />
                          </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-validation-email-invalid">Email Invalid</Label>
                          <Input
                            id="download-validation-email-invalid"
                            value={downloadFormContent.validationMessages.emailInvalid}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, emailInvalid: e.target.value }
                            }))}
                            placeholder="Please enter a valid email"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="download-validation-phone">Phone Required</Label>
                          <Input
                            id="download-validation-phone"
                            value={downloadFormContent.validationMessages.phoneRequired}
                            onChange={(e) => setDownloadFormContent(prev => ({
                              ...prev,
                              validationMessages: { ...prev.validationMessages, phoneRequired: e.target.value }
                            }))}
                            placeholder="Phone number is required"
                          />
                        </div>
                    </div>
                    </div>
                    
                    {/* Success and Error Messages */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Success & Error Messages</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                          <Label htmlFor="download-success-title">Success Title</Label>
                      <Input
                            id="download-success-title"
                            value={downloadFormContent.success.title}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              success: { ...prev.success, title: e.target.value }
                            }))}
                            placeholder="Thank You!"
                      />
                    </div>
                    
                    <div className="space-y-2">
                          <Label htmlFor="download-success-description">Success Description</Label>
                          <Textarea
                            id="download-success-description"
                            value={downloadFormContent.success.description}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              success: { ...prev.success, description: e.target.value }
                            }))}
                            placeholder="Your brochure request has been received. We'll send it to your email shortly."
                            rows={2}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-error-title">Error Title</Label>
                          <Input
                            id="download-error-title"
                            value={downloadFormContent.error.title}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              error: { ...prev.error, title: e.target.value }
                            }))}
                            placeholder="Submission Error"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="download-error-description">Error Description</Label>
                          <Textarea
                            id="download-error-description"
                            value={downloadFormContent.error.description}
                            onChange={(e) => setDownloadFormContent(prev => ({ 
                              ...prev, 
                              error: { ...prev.error, description: e.target.value }
                            }))}
                            placeholder="Failed to submit the request. Please try again later."
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Brochure Upload Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">📄 Brochure Upload</h3>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700 mb-2">
                          <strong>📎 Brochure Management:</strong> Upload a PDF brochure that will be automatically sent to users who submit the download form.
                        </p>
                        <p className="text-xs text-blue-600">
                          Supported format: PDF (max 10MB)
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        {/* File Upload Area */}
                        <div className="space-y-2">
                          <Label htmlFor="brochure-upload">Upload Brochure (PDF)</Label>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            {brochureFile ? (
                              <div className="space-y-3">
                                <div className="flex items-center justify-center space-x-2">
                                  <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-sm font-medium text-gray-700">{brochureFileName}</span>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {(brochureFile.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                                
                                {isBrochureUploading && (
                                  <div className="space-y-2">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                        style={{ width: `${brochureUploadProgress}%` }}
                                      ></div>
                                    </div>
                                    <p className="text-xs text-gray-600">Uploading... {brochureUploadProgress}%</p>
                                  </div>
                                )}
                                
                                <div className="flex justify-center space-x-2">
                                  {!brochureFileId && !isBrochureUploading && (
                                    <Button 
                                      onClick={uploadBrochureFile}
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      Upload Brochure
                                    </Button>
                                  )}
                                  
                                  {brochureFileId && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      ✓ Uploaded Successfully
                                    </span>
                                  )}
                                  
                                  <Button 
                                    onClick={removeBrochureFile}
                                    variant="outline"
                                    size="sm"
                                    disabled={isBrochureUploading}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div>
                                  <label htmlFor="brochure-upload" className="cursor-pointer">
                                    <span className="mt-2 block text-sm font-medium text-gray-900">
                                      Click to upload brochure
                                    </span>
                                    <span className="mt-1 block text-xs text-gray-500">
                                      PDF up to 10MB
                                    </span>
                                  </label>
                                  <input
                                    id="brochure-upload"
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={handleBrochureFileSelect}
                                    className="sr-only"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {brochureFileId && (
                          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                            <p className="text-xs text-green-700">
                              <strong>✓ Ready:</strong> This brochure will be automatically sent to users who submit the download form.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email Template Management - Brochure Email */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">📧 Brochure Email Template</h3>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700 mb-2">
                          <strong>📬 Automatic Email System:</strong> When users submit the download form, they automatically receive a personalized email with the brochure download link.
                        </p>
                        <p className="text-xs text-blue-600">
                          Available variables: {`{{name}}, {{fullName}}, {{projectName}}, {{projectLink}}, {{brochureLink}}`}
                        </p>
                      </div>
                      
                      {/* Fallback indicator */}
                      {brochureTemplateWithFallback?.fallbackUsed && (
                        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-orange-900">
                                Using Template Fallback
                              </p>
                              <p className="text-xs text-orange-700 mt-1">
                                {brochureTemplateWithFallback.fallbackType === 'latest_language' && 
                                  `No template exists for this project in ${selectedLanguage}. Using the latest ${selectedLanguage} template from another project.`}
                                {brochureTemplateWithFallback.fallbackType === 'project_english' && 
                                  `No ${selectedLanguage} template found. Using English template from this project.`}
                                {brochureTemplateWithFallback.fallbackType === 'latest_english' && 
                                  `No ${selectedLanguage} templates found. Using the latest English template from another project.`}
                              </p>
                              <p className="text-xs text-orange-600 mt-1">
                                <strong>Tip:</strong> Save this template to create a {selectedLanguage}-specific version for this project.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="brochure-email-subject">Email Subject</Label>
                          <Input
                            id="brochure-email-subject"
                            value={brochureEmailTemplateContent.subject}
                            onChange={(e) => setBrochureEmailTemplateContent(prev => ({ 
                              ...prev, 
                              subject: e.target.value 
                            }))}
                            placeholder="Your {{projectName}} Brochure is Ready!"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="brochure-email-html">🎨 HTML Email Content</Label>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Primary - Rich formatting</span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">
                            This is the main email version with colors, styling, and download button that most users will see.
                          </p>
                          <Textarea
                            id="brochure-email-html"
                            value={brochureEmailTemplateContent.htmlContent}
                            onChange={(e) => setBrochureEmailTemplateContent(prev => ({ 
                              ...prev, 
                              htmlContent: e.target.value 
                            }))}
                            placeholder="HTML email template with brochure download link..."
                            rows={8}
                            className="font-mono text-sm"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="brochure-email-text">📝 Plain Text Email Content</Label>
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Fallback - Accessibility</span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">
                            Simple text version for older email clients. Auto-generated if left empty.
                          </p>
                          <Textarea
                            id="brochure-email-text"
                            value={brochureEmailTemplateContent.textContent}
                            onChange={(e) => setBrochureEmailTemplateContent(prev => ({ 
                              ...prev, 
                              textContent: e.target.value 
                            }))}
                            placeholder="Plain text version (optional - will be auto-generated from HTML if empty)..."
                            rows={6}
                          />
                        </div>
                        
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <p className="text-xs text-yellow-700">
                            <strong>💡 Tip:</strong> The {"{{brochureLink}}"} variable will be replaced with a secure download link for the PDF brochure.
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="brochure-email-active"
                            checked={brochureEmailTemplateContent.isActive}
                            onCheckedChange={(checked) => setBrochureEmailTemplateContent(prev => ({ 
                              ...prev, 
                              isActive: checked 
                            }))}
                          />
                          <Label htmlFor="brochure-email-active">Brochure email template is active</Label>
                        </div>
                        
                        <div className="flex justify-end">
                          <Button 
                            onClick={saveBrochureEmailTemplate}
                            disabled={isLoading}
                            variant="outline"
                            size="sm"
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                              </>
                            ) : '💾 Save Brochure Email Template'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={resetDownloadFormToDefault}
                      disabled={isLoading}
                    >
                      Reset to Default
                    </Button>
                    <Button 
                      onClick={saveDownloadFormComponent}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : 'Save Download Form'}
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>
              
              <TabsContent value="leadForm" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Lead Form</CardTitle>
                    <CardDescription>
                      Configure the lead capture form for your project. All text fields support multilingual content.
                    </CardDescription>
                    {selectedLanguage !== 'en' && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-amber-900">
                              Manual Translation Required
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                              Forms are excluded from auto-translation. Please manually translate all form content for this language.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Basic Form Settings */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Basic Settings</h3>
                      
                      <div className="space-y-2">
                        <Label htmlFor="leadform-title">Form Title</Label>
                        <RTLInput
                          id="leadform-title"
                          language={selectedLanguage}
                          value={leadFormContent.title}
                          onChange={(e) => setLeadFormContent(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Get in Touch"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="leadform-description">Form Description</Label>
                        <RTLTextarea
                          id="leadform-description"
                          language={selectedLanguage}
                          value={leadFormContent.description}
                          onChange={(e) => setLeadFormContent(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Contact us to learn more about this project and schedule a viewing."
                          rows={3}
                        />
                      </div>
                      </div>
                      
                    {/* Step Titles and Descriptions */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Step Titles</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                          <Label htmlFor="step-title-basic">Basic Info Step Title</Label>
                        <RTLInput
                            id="step-title-basic"
                            language={selectedLanguage}
                            value={leadFormContent.stepTitles.basicInfo}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepTitles: { ...prev.stepTitles, basicInfo: e.target.value }
                            }))}
                            placeholder="Let's get to know you"
                        />
                      </div>
                      
                      <div className="space-y-2">
                          <Label htmlFor="step-desc-basic">Basic Info Step Description</Label>
                          <RTLInput
                            id="step-desc-basic"
                            language={selectedLanguage}
                            value={leadFormContent.stepDescriptions.basicInfo}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepDescriptions: { ...prev.stepDescriptions, basicInfo: e.target.value }
                            }))}
                            placeholder="Enter your details to receive personalized information"
                        />
                      </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="step-title-purpose">Purpose Step Title</Label>
                          <Input
                            id="step-title-purpose"
                            value={leadFormContent.stepTitles.purpose}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepTitles: { ...prev.stepTitles, purpose: e.target.value }
                            }))}
                            placeholder="What brings you here today?"
                          />
                    </div>
                    
                        <div className="space-y-2">
                          <Label htmlFor="step-desc-purpose">Purpose Step Description</Label>
                          <Input
                            id="step-desc-purpose"
                            value={leadFormContent.stepDescriptions.purpose}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepDescriptions: { ...prev.stepDescriptions, purpose: e.target.value }
                            }))}
                            placeholder="Select your primary interest"
                          />
                      </div>
                      
                        <div className="space-y-2">
                          <Label htmlFor="step-title-investment">Investment Step Title</Label>
                          <Input
                            id="step-title-investment"
                            value={leadFormContent.stepTitles.investment}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepTitles: { ...prev.stepTitles, investment: e.target.value }
                            }))}
                            placeholder="Investment Preferences"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="step-desc-investment">Investment Step Description</Label>
                          <Input
                            id="step-desc-investment"
                            value={leadFormContent.stepDescriptions.investment}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepDescriptions: { ...prev.stepDescriptions, investment: e.target.value }
                            }))}
                            placeholder="Tell us about your investment preferences"
                          />
                    </div>
                    
                        <div className="space-y-2">
                          <Label htmlFor="step-title-enduser">End User Step Title</Label>
                          <Input
                            id="step-title-enduser"
                            value={leadFormContent.stepTitles.endUser}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepTitles: { ...prev.stepTitles, endUser: e.target.value }
                            }))}
                            placeholder="Your Dream Home"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="step-desc-enduser">End User Step Description</Label>
                          <Input
                            id="step-desc-enduser"
                            value={leadFormContent.stepDescriptions.endUser}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepDescriptions: { ...prev.stepDescriptions, endUser: e.target.value }
                            }))}
                            placeholder="Tell us about your home preferences"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="step-title-contact">Contact Step Title</Label>
                          <Input
                            id="step-title-contact"
                            value={leadFormContent.stepTitles.contact}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepTitles: { ...prev.stepTitles, contact: e.target.value }
                            }))}
                            placeholder="How would you like us to contact you?"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="step-desc-contact">Contact Step Description</Label>
                          <Input
                            id="step-desc-contact"
                            value={leadFormContent.stepDescriptions.contact}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              stepDescriptions: { ...prev.stepDescriptions, contact: e.target.value }
                            }))}
                            placeholder="Choose your preferred contact method"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Purpose Option Descriptions */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Purpose Option Descriptions</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="purpose-investment-desc">Investment Option Description</Label>
                          <Textarea
                            id="purpose-investment-desc"
                            value={leadFormContent.purposeDescriptions.investment}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              purposeDescriptions: { ...prev.purposeDescriptions, investment: e.target.value }
                            }))}
                            placeholder="Looking for a profitable investment opportunity in Dubai's growing real estate market"
                            rows={3}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="purpose-enduser-desc">End User Option Description</Label>
                          <Textarea
                            id="purpose-enduser-desc"
                            value={leadFormContent.purposeDescriptions.endUser}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              purposeDescriptions: { ...prev.purposeDescriptions, endUser: e.target.value }
                            }))}
                            placeholder="Searching for your perfect home in Dubai to live in"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Option Descriptions */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Contact Option Descriptions</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="whatsapp-desc">WhatsApp Description</Label>
                          <Textarea
                            id="whatsapp-desc"
                            value={leadFormContent.contactDescriptions.whatsapp}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              contactDescriptions: { ...prev.contactDescriptions, whatsapp: e.target.value }
                            }))}
                            placeholder="Enter a brief description for WhatsApp"
                            rows={2}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="phone-desc">Phone Description</Label>
                          <Textarea
                            id="phone-desc"
                            value={leadFormContent.contactDescriptions.phone}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              contactDescriptions: { ...prev.contactDescriptions, phone: e.target.value }
                            }))}
                            placeholder="Enter a brief description for phone calls"
                            rows={2}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="email-desc">Email Description</Label>
                          <Textarea
                            id="email-desc"
                            value={leadFormContent.contactDescriptions.email}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              contactDescriptions: { ...prev.contactDescriptions, email: e.target.value }
                            }))}
                            placeholder="Enter a brief description for email inquiries"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Validation Messages */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Validation Messages</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="lead-validation-name">Name Required</Label>
                            <Input
                            id="lead-validation-name"
                            value={leadFormContent.validationMessages.nameRequired}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                                ...prev,
                              validationMessages: { ...prev.validationMessages, nameRequired: e.target.value }
                              }))}
                            placeholder="Please enter your full name"
                            />
                          </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="lead-validation-email">Email Required</Label>
                          <Input
                            id="lead-validation-email"
                            value={leadFormContent.validationMessages.emailRequired}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, emailRequired: e.target.value }
                            }))}
                            placeholder="Please enter your email address"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="lead-validation-email-invalid">Email Invalid</Label>
                          <Input
                            id="lead-validation-email-invalid"
                            value={leadFormContent.validationMessages.emailInvalid}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, emailInvalid: e.target.value }
                            }))}
                            placeholder="Please enter a valid email address"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="lead-validation-phone">Phone Required</Label>
                          <Input
                            id="lead-validation-phone"
                            value={leadFormContent.validationMessages.phoneRequired}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, phoneRequired: e.target.value }
                            }))}
                            placeholder="Please enter a valid phone number"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="lead-validation-phone-invalid">Phone Invalid</Label>
                          <Input
                            id="lead-validation-phone-invalid"
                            value={leadFormContent.validationMessages.phoneInvalid}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              validationMessages: { ...prev.validationMessages, phoneInvalid: e.target.value }
                            }))}
                            placeholder="Please enter a valid phone number"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Budget Options */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium border-b pb-2">Budget Options</h3>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={addBudgetOption}
                          type="button"
                        >
                          Add Budget Option
                        </Button>
                      </div>
                      
                      {leadFormContent.budgetOptions.map((option, index) => (
                        <div key={index} className="grid grid-cols-2 gap-2">
                          <Input
                            value={option.value}
                            onChange={(e) => updateBudgetOption(index, 'value', e.target.value)}
                            placeholder="Value (e.g., 500k-1m)"
                          />
                          <div className="flex items-center space-x-2">
                          <Input
                            value={option.label}
                            onChange={(e) => updateBudgetOption(index, 'label', e.target.value)}
                            placeholder="Label (e.g., AED 500K - 1M)"
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeBudgetOption(index)}
                            className="text-red-500"
                          >
                            Remove
                          </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Timeline Options */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium border-b pb-2">Timeline Options</h3>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={addTimelineOption}
                          type="button"
                        >
                          Add Timeline Option
                        </Button>
                      </div>
                      
                      {leadFormContent.timelineOptions.map((option, index) => (
                        <div key={index} className="grid grid-cols-2 gap-2">
                          <Input
                            value={option.value}
                            onChange={(e) => updateTimelineOption(index, 'value', e.target.value)}
                            placeholder="Value (e.g., immediate)"
                          />
                          <div className="flex items-center space-x-2">
                          <Input
                            value={option.label}
                            onChange={(e) => updateTimelineOption(index, 'label', e.target.value)}
                            placeholder="Label (e.g., Immediate)"
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeTimelineOption(index)}
                            className="text-red-500"
                          >
                            Remove
                          </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Bedroom Options */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium border-b pb-2">Bedroom Options</h3>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={addBedroomOption}
                          type="button"
                        >
                          Add Bedroom Option
                        </Button>
                      </div>
                      
                      {leadFormContent.bedroomOptions.map((option, index) => (
                        <div key={index} className="grid grid-cols-2 gap-2">
                          <Input
                            value={option.value}
                            onChange={(e) => updateBedroomOption(index, 'value', e.target.value)}
                            placeholder="Value (e.g., studio)"
                          />
                          <div className="flex items-center space-x-2">
                          <Input
                            value={option.label}
                            onChange={(e) => updateBedroomOption(index, 'label', e.target.value)}
                            placeholder="Label (e.g., Studio)"
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeBedroomOption(index)}
                            className="text-red-500"
                          >
                            Remove
                          </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Success and Error Messages */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">Success & Error Messages</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="lead-success-title">Success Title</Label>
                          <Input
                            id="lead-success-title"
                            value={leadFormContent.success.title}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              success: { ...prev.success, title: e.target.value }
                            }))}
                            placeholder="Thank You!"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="lead-success-description">Success Description</Label>
                          <Textarea
                            id="lead-success-description"
                            value={leadFormContent.success.description}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              success: { ...prev.success, description: e.target.value }
                            }))}
                            placeholder="Thank you for your interest! We will contact you soon."
                            rows={2}
                          />
                      </div>
                      
                        <div className="space-y-2">
                          <Label htmlFor="lead-error-title">Error Title</Label>
                          <Input
                            id="lead-error-title"
                            value={leadFormContent.error.title}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              error: { ...prev.error, title: e.target.value }
                            }))}
                            placeholder="Submission Error"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="lead-error-description">Error Description</Label>
                          <Textarea
                            id="lead-error-description"
                            value={leadFormContent.error.description}
                            onChange={(e) => setLeadFormContent(prev => ({ 
                              ...prev, 
                              error: { ...prev.error, description: e.target.value }
                            }))}
                            placeholder="Failed to submit the form. Please try again later."
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Email Template Management */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium border-b pb-2">📧 Welcome Email Template</h3>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700 mb-2">
                          <strong>📬 Automatic Email System:</strong> When users submit the lead form, they automatically receive a personalized welcome email.
                        </p>
                        <p className="text-xs text-blue-600">
                          Available variables: {`{{name}}, {{fullName}}, {{projectName}}, {{projectLink}}`}
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="email-subject">Email Subject</Label>
                          <Input
                            id="email-subject"
                            value={emailTemplateContent.subject}
                            onChange={(e) => setEmailTemplateContent(prev => ({ 
                              ...prev, 
                              subject: e.target.value 
                            }))}
                            placeholder="Welcome to {{projectName}} - We'll be in touch soon!"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="email-html">🎨 HTML Email Content</Label>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Primary - Rich formatting</span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">
                            This is the main email version with colors, styling, and buttons that most users will see.
                          </p>
                          <Textarea
                            id="email-html"
                            value={emailTemplateContent.htmlContent}
                            onChange={(e) => setEmailTemplateContent(prev => ({ 
                              ...prev, 
                              htmlContent: e.target.value 
                            }))}
                            placeholder="HTML email template with styling..."
                            rows={8}
                            className="font-mono text-sm"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="email-text">📝 Plain Text Email Content</Label>
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Fallback - Accessibility</span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">
                            Simple text version for older email clients and accessibility tools. Auto-generated if left empty.
                          </p>
                          <Textarea
                            id="email-text"
                            value={emailTemplateContent.textContent}
                            onChange={(e) => setEmailTemplateContent(prev => ({ 
                              ...prev, 
                              textContent: e.target.value 
                            }))}
                            placeholder="Plain text version (optional - will be auto-generated from HTML if empty)..."
                            rows={6}
                          />
                        </div>
                        
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <p className="text-xs text-yellow-700">
                            <strong>💡 Tip:</strong> Focus on the HTML version for design and styling. The text version is mainly for compatibility - you can leave it empty and it will be auto-generated.
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="email-active"
                            checked={emailTemplateContent.isActive}
                            onCheckedChange={(checked) => setEmailTemplateContent(prev => ({ 
                              ...prev, 
                              isActive: checked 
                            }))}
                          />
                          <Label htmlFor="email-active">Email template is active</Label>
                        </div>
                        
                        <div className="flex justify-end">
                          <Button 
                            onClick={saveEmailTemplate}
                            disabled={isLoading}
                            variant="outline"
                            size="sm"
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                              </>
                            ) : '💾 Save Email Template'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={resetLeadFormToDefault}
                      disabled={isLoading}
                    >
                      Reset to Default
                    </Button>
                    <Button 
                      onClick={saveLeadFormComponent}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : 'Save Lead Form'}
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>
              
              <TabsContent value="faq" className="space-y-6">
                <FaqSection
                  projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  faqComponent={faqComponent}
                  faqContent={faqContent}
                  setFaqContent={setFaqContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
              
              <TabsContent value="legal" className="space-y-6">
                <LegalInfoSection
                  projectId={projectId || ''}
                  selectedLanguage={selectedLanguage}
                  legalInfoComponent={legalInfoComponent}
                  legalInfoContent={legalInfoContent}
                  setLegalInfoContent={setLegalInfoContent}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  refreshComponents={refreshComponents}
                />
              </TabsContent>
            </Tabs>
          </div>

          {showPreview && (
            <div className="w-1/2 border rounded-lg overflow-hidden">
              <div className="h-[600px] overflow-auto relative">
                 
              </div>
              <div className="bg-gray-100 p-4 text-center text-sm text-gray-500">
                Preview (scrollable)
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 